/**
 * PlotRenderer — the inline chart dispatcher for a Quick Study insight.
 *
 * Extracted from QuickStudyPage.tsx as a self-contained real seam: it renders the one
 * best-fit deck-ui chart for a result's plot id (honoring a render-only plot override),
 * plus the NPS diverging bar and radar/NPS-bucket reshapers it needs. Pure presentational
 * — every input arrives via props; no Quick Study page state is closed over.
 *
 * Types (Result/SurveyQuestion/TwinResponse) are imported type-only from QuickStudyPage
 * (their single source); the import is erased at runtime, so there is no module cycle.
 */
import { createContext, useContext, useMemo, type ComponentProps, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import {
  RankingChart,
  NumericChart,
  SingleChoiceChart,
  MatrixChart,
  QuadrantChart,
  ChartJsHorizontalBar,
  TrackBar,
  DivergingBar,
  PlotDotLegend,
} from '@digital-twins/deck-ui/components'
import { RadarChart } from '@digital-twins/deck-ui/components/charts/RadarChart/RadarChart'
import type { RadarSeries } from '@digital-twins/deck-ui/components/charts/RadarChart/RadarChart'
import { rankingChartLabelsForLanguage } from '@/lib/charts/rankingChartLabels'
import { getAppChartPalette } from '@/lib/charts/chartjs'
import { useContentLanguage } from '@/i18n/contentLanguageContext'
import { cn } from '@/lib/utils'
import { qualifiedCutDisplayLabel } from './audience-segments'
import { PSMChart } from './PSMChart'
import { PriceCurvesPlot, PriceCurvesRefusal, quickStudyPriceCurvesAdapter } from '@digital-twins/deck-ui/plots/price-curves'
import { QuadrantPlot, quadrantQuickStudyAdapter } from '@digital-twins/deck-ui/plots/quadrant'
import { npsGroupOf, npsSplit } from '@digital-twins/deck-ui/plots/diverging-likert/nps'
import { formatNpsScore } from '@/features/studies/report/page/components/tabs/formatAbsoluteScore'
import { usePriceCurveRefusalText, usePriceCurvesLabels } from '@/features/studies/report/page/components/views/analysis/usePriceCurvesLabels'
import { DensityHistogramPlot } from '@digital-twins/deck-ui/plots/density-histogram'
import { ChartLabel } from '@digital-twins/deck-ui/plots/_shared'
import { useDensityHistogramLabels } from '@/features/studies/report/page/components/views/analysis/useDensityHistogramLabels'
import { quickStudyRankingAdapter } from '@digital-twins/deck-ui/plots/ranking-bars'
import { RadarPlot, quickStudyScorecardRadarAdapter } from '@digital-twins/deck-ui/plots/radar'
import { MigrationMatrixChart } from './MigrationMatrixChart'
import { DivergingStackedRating, ScorecardHeatmap, type DivergingChromeData } from './DivergingStackedRating'
import { QuickStudyOpenEndCard, CHART_BAR_TRACK_COLOR } from './QuickStudyOpenEndCard'
import { MonadicOpenEndCard } from './MonadicOpenEndCard'
import { collectComparisonLabels, buildAuthoredSegmentGroups, sliceResponsesByCutIds, monadicRatingSegmentsByCuts, honestyThreadsSuffix, stripLabelQuotes, buildMonadicVersionLabelMap } from './quickStudyPure'
import type { CutIdSlice, MonadicSegmentGroup } from './quickStudyPure'
import { breakdownRows, hasThinCells, segmentedMode, supportsSegments, SEGMENTS_UNAVAILABLE_NOTE } from './segmentBreakdown'
import { orderedSegBands, SegmentSwap, CutToggle, useActiveBand, BandCaveat, SuppressedCutsNote, type SegBand } from './segmentedToggle'
import type { CompareSegmentsGroup } from './CompareSegmentsPicker'
import type { QuickStudyPersona } from './QuickStudyTwinsPanel'
import type { Result, SurveyQuestion, TwinResponse } from './quickStudyTypes'
import { FindingServerSpecPlot } from './featuredPlots/FindingServerSpecPlot'
import { QuickStudyChoicePlot, hasQuickStudyChoicePlot } from './QuickStudyChoicePlot'
import { QuickStudyLikertPlot, hasQuickStudyLikertPlot, likertViewForMetric } from './QuickStudyLikertPlot'
import { QuickStudyGridPlot, hasQuickStudyGridPlot, hasQuickStudyScorecardPlot } from './QuickStudyGridPlot'
import { QuickStudyMonadicPlot, hasQuickStudyMonadicPlot } from './QuickStudyMonadicPlot'
import { QuickStudyRankingPlot, QuickStudyRankingView } from './QuickStudyRankingPlot'
import { CutPlotsError } from './CutPlotsError'
import { stampedPlotOffers } from './quickStudyStatViews'
import { useQuickStudyCutPlots } from './quickStudyCutPlots'
import { ChartSkeleton } from '@digital-twins/deck-ui/components'
import {
  analystSpecsFromResult,
  findingSpecsCarryPlots,
  shouldPreferAnalystSpec,
} from './featuredPlots/findingAnalystSpecs'
import { reportFindingPlotParity } from './featuredPlots/findingPlotParity'

// Every QuickStudy horizontal bar renders with a full-width rounded TRACK behind each bar
// (matching the HTML `ThemeBarRow` bars: a muted pill the colored fill sits on), so the
// canvas bars read consistently with the DOM bars in the same card. Thin wrapper so the
// nine call sites below stay declarative; callers may still override `showTrack`.
function HBar({ colors, ...props }: ComponentProps<typeof ChartJsHorizontalBar>) {
  // Every HBar here is a single-series distribution (one bar per category), so all bars
  // share ONE color instead of cycling the multi-color palette. Collapse the passed
  // palette to its primary; the single-color array makes ChartJsHorizontalBar paint every
  // bar the same. Segmented/categorical comparisons use other components, not HBar.
  const uniformColors = colors && colors.length ? [colors[0]] : colors
  return <ChartJsHorizontalBar showTrack trackColor={CHART_BAR_TRACK_COLOR} colors={uniformColors} {...props} />
}

// SurveyJS booleans serialize as raw true/false, which the chart stringifies to
// "True"/"False" — jarring for a marketer reading a yes/no question. Remap the
// question to a Yes/No choice question (and the responses to match) so every plot
// path labels cleanly. Plot-agnostic; leaves the shared deck-ui aggregation alone.
function yesNoize(question: any, responses: any[]): { question: any; responses: any[] } {
  if (question?.type !== 'boolean') return { question, responses }
  const name = question.name
  const q = {
    ...question,
    type: 'radiogroup',
    choices: [
      { value: 'Yes', text: 'Yes' },
      { value: 'No', text: 'No' },
    ],
  }
  const resp = responses.map((row) => {
    const v = row?.survey_data?.[name]
    if (typeof v !== 'boolean') return row
    return { ...row, survey_data: { ...row.survey_data, [name]: v ? 'Yes' : 'No' } }
  })
  return { question: q, responses: resp }
}

// F7a — a choice round (price ladder / packaging tournament) attaches a "None of the
// above" REJECTER option. Its option id is a generic none-token (e.g. "none"), which
// collides with deck-ui's don't-know filter (chart-aggregation's isDontKnowValue drops
// "none"/"na"/"n/a"/""). So every twin who rejected every paid offer is silently dropped
// from the tally: the pie seeds the None slice at 0 (includeEmptyChoices) but never
// increments it, showing None = 0% while the verdict says e.g. 20% would not move forward.
//
// FIX: detect the None option GENERICALLY via option_metadata.is_none (mirrors python's
// `_none_option_ids` — keyed on the flag/id, never on the label) and remap that option's
// choice value + the matching responses to a filter-safe sentinel. The slice then tallies
// the real rejecter share, and the pie's denominator (paid + none) matches the python
// `none_share_pct` headline. No-op for any question without an is_none option (the vast
// majority), so ordinary surveys are untouched. Plot-agnostic: applied before the switch.
export function reconcileNoneSlice(
  question: any,
  responses: any[],
  optionMetadata?: Array<{ id: string; is_none?: boolean }>,
): { question: any; responses: any[] } {
  if (question?.type !== 'radiogroup' && question?.type !== 'checkbox') return { question, responses }
  const noneIds = new Set(
    (optionMetadata ?? []).filter((o) => Boolean(o?.is_none)).map((o) => String(o.id)),
  )
  if (noneIds.size === 0) return { question, responses }
  const name = question.name
  // Sentinel keeps the original id embedded so the choice still resolves its label, while
  // no longer matching the don't-know filter's literal "none"/"na"/"" set.
  const safe = (id: string): string => `__none_${id}__`
  const choices = Array.isArray(question.choices) ? question.choices : []
  const q = {
    ...question,
    choices: choices.map((c: any) => {
      if (!c || typeof c !== 'object') return c
      const val = String(c.value ?? c)
      return noneIds.has(val) ? { ...c, value: safe(val) } : c
    }),
  }
  const remap = (v: any): any => (typeof v === 'string' && noneIds.has(v) ? safe(v) : v)
  const resp = responses.map((row) => {
    const v = row?.survey_data?.[name]
    if (v === undefined) return row
    if (typeof v === 'string') {
      if (!noneIds.has(v)) return row
      return { ...row, survey_data: { ...row.survey_data, [name]: safe(v) } }
    }
    if (Array.isArray(v)) {
      if (!v.some((item) => typeof item === 'string' && noneIds.has(item))) return row
      return { ...row, survey_data: { ...row.survey_data, [name]: v.map(remap) } }
    }
    return row
  })
  return { question: q, responses: resp }
}

// --------------------------------------------------------------------------- //
// Monadic per-item chart helpers: build the MatrixBarDatum[] lists that        //
// ChartJsHorizontalBar renders as a per-item ranked bar. The item with the     //
// highest value is sorted first so the marketer sees the winner at the top.    //
// Each helper is pure and self-contained so per-shape rendering is a one-liner //
// in the switch cases below.                                                   //
// --------------------------------------------------------------------------- //

// A monadic bar/axis label must NEVER render blank (QS display fix W0.1): an item whose
// authored `label` arrives empty or whitespace-only would otherwise paint a value bar with no
// tick beside it (the "unlabeled 39% bar" defect). Fall back deterministically to the item's
// stable id, then to a positional "Item N" so every bar is always attributable. W0.3: axis
// ticks drop a WRAPPING quote pair — stored labels may be whole-label-quoted ('"A — Control
// Tower"') and literal quote glyphs on a tick are noise (prose surfaces keep the stored
// quoting; this is display-only for the axis). Pure.
export function safeBarLabel(label: string | undefined | null, id: string | undefined | null, index: number): string {
  const l = stripLabelQuotes(label)
  if (l) return l
  const i = (id ?? '').trim()
  if (i) return i
  return `Item ${index + 1}`
}

/** Per-item rating bar: one bar per item, value = mean or top_2_box (0-100%). */
function buildMonadicRatingBar(
  items: NonNullable<Result['monadic_rating']>,
  metric: string,
): MatrixBarDatum[] {
  const isTop2 = metric === 'top_2_box'
  return [...items]
    .sort((a, b) => (isTop2 ? b.top_2_box - a.top_2_box : b.mean - a.mean))
    .map((it, i) => ({ label: safeBarLabel(it.label, it.id, i), value: isTop2 ? it.top_2_box : it.mean }))
}

/**
 * Monadic rating DISTRIBUTION: for each score point on the scale, how many twins gave EACH
 * item that score — one grouped-bar series per item. Lets the marketer read the head-to-head
 * "how many rated Creative A a 1 vs Creative B a 1" the summary mean bar hides. Pure.
 *   labels = ['1','2',…,scaleMax]; series[i] = { label, values: count-per-score for item i }.
 */
export function buildMonadicRatingDistribution(
  items: NonNullable<Result['monadic_rating']>,
): { labels: string[]; series: Array<{ label: string; values: number[] }>; yMax: number } {
  const scaleMin = items[0]?.scale_min ?? 1
  const scaleMax = items[0]?.scale_max ?? 5
  const scores: number[] = []
  for (let s = scaleMin; s <= scaleMax; s++) {
    scores.push(s)
  }
  let yMax = 0
  const series = items.map((it) => ({
    label: it.label,
    values: scores.map((s) => {
      // distribution keys arrive JSON-stringified ({ "1": 4 }); index tolerantly.
      const count = it.distribution?.[s] ?? (it.distribution as Record<string, number>)?.[String(s)] ?? 0
      if (count > yMax) {
        yMax = count
      }
      return count
    }),
  }))
  return { labels: scores.map((s) => String(s)), series, yMax }
}

/** Per-item yes-rate bar (boolean): one bar per item, value = yes_pct (0-100%). */
function buildMonadicBooleanBar(items: NonNullable<Result['monadic_boolean']>): MatrixBarDatum[] {
  return [...items]
    .sort((a, b) => b.yes_pct - a.yes_pct)
    .map((it, i) => ({ label: safeBarLabel(it.label, it.id, i), value: it.yes_pct }))
}

/** Per-item median bar (numeric): one bar per item, value = median. */
function buildMonadicNumericBar(items: NonNullable<Result['monadic_numeric']>): MatrixBarDatum[] {
  return [...items]
    .sort((a, b) => b.median - a.median)
    .map((it, i) => ({ label: safeBarLabel(it.label, it.id, i), value: it.median }))
}

/** Per-item top-choice bar (radiogroup/checkbox): one bar per item, value = top-choice %. */
function buildMonadicChoiceBar(items: NonNullable<Result['monadic_choice']>): MatrixBarDatum[] {
  // Each item's "score" = the select-rate of its most-selected choice.
  return [...items]
    .sort((a, b) => (b.choices[0]?.pct ?? 0) - (a.choices[0]?.pct ?? 0))
    .map((it, i) => ({
      label: safeBarLabel(it.label, it.id, i),
      value: it.choices[0]?.pct ?? 0,
    }))
}

// Chat-only (`intentView`) DOM magnitude list: the simple 0–100% magnitude exhibits
// (ranking #1-share, monadic yes-rate, monadic top-choice select-rate) render as a DOM
// `TrackBar` list — one row per item (label · value · pct-width, single accent hue) —
// instead of the canvas `HBar`, so the chat card reads consistently with the other DOM
// bars (token track + floating tooltip, no x-axis needed for a per-row percentage). The
// report/deck (non-`intentView`) call sites keep the canvas `HBar` untouched.
function MagnitudeBarList({ data, color }: { data: MatrixBarDatum[]; color: string }) {
  return (
    <div className="w-full space-y-5">
      {data.map((d) => {
        const pct = Math.round(d.value)
        return (
          <div key={d.label} className="space-y-1.5">
            <div className="flex items-center justify-between gap-4">
              <div className="min-w-0 text-[14px] font-normal leading-snug text-twin-chat-fg [overflow-wrap:anywhere]">
                {d.label}
              </div>
              <div className="shrink-0 text-right">
                <span className="text-sm font-semibold tabular-nums">{pct}%</span>
              </div>
            </div>
            <TrackBar widthPct={d.value} color={color} value={`${pct}%`} label={d.label} />
          </div>
        )
      })}
    </div>
  )
}

/** SurveyJS open-end shapes — must NOT fall through to SingleChoiceChart (raw verbatim bars). */
function isOpenEndQuestion(question: any): boolean {
  const t = question?.type
  return t === 'comment' || t === 'text'
}

// The plot ids with an EXPLICIT switch case (below) that renders a QUANT exhibit even when the
// question is open-end (comment/text) — pie/ranking/rating/histogram/radar/grouped_bar/quadrant/
// matrix/heatmap/nps_diverging/psm/migration_matrix/diverging_stacked. Only the `bar` case and any
// UNRECOGNIZED plot id (the `default` case) fall through to `renderOpenEndInsights` on an open-end
// question — so a card routes to the OE renderer iff the question is open-end AND its plot is NOT in
// this set. Keep in sync with the switch cases in `PlotRenderer`.
const EXPLICIT_QUANT_PLOTS = new Set<string>([
  'migration_matrix',
  'ranking',
  'pie',
  'diverging_stacked',
  'rating',
  'histogram',
  'quadrant',
  'matrix',
  'heatmap',
  'nps_diverging',
  'radar',
  'grouped_bar',
  'psm',
])

// T4 (Fable review) — does this (plot, question) pair actually route to `renderOpenEndInsights`?
// True only when the question is open-end AND the plot falls through to the OE renderer (the `bar`
// case, the OE plot-id cases, or the `default` case) — NOT an explicit-quant case that renders a
// chart even for a comment question. MessageRow uses this to scope the OE chat concerns (lifted
// toggle, themes-only export, quotes picker) to the cards that genuinely render open-end.
/** A single / multi choice question (the reads Python stamps a `choice_plot` for). */
function isChoiceQuestion(question: any): boolean {
  return question?.type === 'radiogroup' || question?.type === 'dropdown' || question?.type === 'checkbox'
}

export function routesToOpenEnd(plot: string, question: any): boolean {
  return isOpenEndQuestion(question) && !EXPLICIT_QUANT_PLOTS.has(plot)
}

// Monadic methodology note (R10 chrome) — the chat-only "Each creative was shown to N twins on
// its own" methodology line, now composed as a pure string so MessageRow can render it in the
// PlotCard `footnote` slot (the OE card converges into PlotCard on chat) and this module can drop
// the inline render. The true-monadic honesty suffix ('' unless python reports a twin-monadic
// reuse fallback, n_reused_threads > 0) rides on the note so a shared-twin run still discloses it.
// The report card states its base via the per-card coverage footer instead, so it never used this.
export function monadicCoverageNote(r: Result): string {
  const nItems = r.per_item?.length ?? 0
  // Guard the `r.total / nItems` fallback against div-by-zero: with no per-item breakdown
  // (`nItems <= 0`) skip the derived split and leave `nPer` null (T6).
  const nPer = r.n_per_creative ?? (r.total && nItems > 0 ? Math.round(r.total / nItems) : null)
  const noun = r.item_noun ?? 'creative'
  const nounPlural = `${noun}s`
  const shownTo = nPer != null ? ` ${nPer}` : ''
  const breakdown = nPer != null && nItems > 1 ? ` (${nPer} × ${nItems} ${nounPlural})` : ''
  return `Each ${noun} was shown to${shownTo} twins on its own${breakdown}${honestyThreadsSuffix(r.n_unique_respondents, r.n_reused_threads)}.`
}

/** Themes + quotes card (and monadic open-end variant). Shared across plot id aliases. */
function renderOpenEndInsights(
  r: Result,
  q: any,
  resp: TwinResponse[],
  personas: QuickStudyPersona[] | undefined,
  comparisonLabels: string[],
  // Population/audience id the run's twins belong to. Threaded straight through to the
  // plain-OE QuickStudyOpenEndCard below so a Quotes-view quote still missing an image
  // after persona/attribute resolution can ask the slim picker view instead of staying
  // on initials. Optional — omitted preserves today's behavior.
  populationId?: string,
  onJumpToSource?: () => void,
  creativeTitles: string[] = [],
  inReport = false,
  openEndTab?: 'themes' | 'quotes',
  onOpenEndTabChange?: (t: 'themes' | 'quotes') => void,
  // Step 7 (review F2) — the pre-sliced quote UNION for the plain OE card's QUOTES view. The
  // sliced responses can only reach the quotes view through this channel (renderOpenEndInsights
  // derives `resp` internally from `r.responses`, and the generic `selectedCuts`/`cutSlices` path
  // is ignored by the OE branch). Chat path only; when present it feeds the QuotesView INSTEAD of
  // the full `resp` — the THEMES bars keep the full card-level aggregates (`r.themes`), and the
  // monadic card is untouched (its quotes come from `per_item[].responses`, review F3).
  openEndQuoteResponses?: Array<TwinResponse>,
  // T3 — true when `openEndQuoteResponses` was sliced to EMPTY by a cut (a segment that matched no
  // twins), so the plain OE QuotesView renders "No verbatims match the selected segments." instead
  // of the false "None captured" copy. Chat path only; the monadic card and report are untouched.
  openEndQuoteFilteredEmpty = false,
  themesView: 'heatmap' | 'bars' = 'heatmap',
) {
  const documentMode = inReport
  // Chat path (not in report): the OE card converges into the outer PlotCard — the Themes|Quotes
  // toggle + construct title live in PlotCard slots upstream, so the card renders in CONTROLLED
  // mode (`activeTab`) with NO internal toggle and NO internal construct header (PlotCard owns the
  // title; the monadic methodology note rides in the PlotCard footnote via `monadicCoverageNote`).
  // In report (`inReport`), `activeTab` stays undefined → the card is uncontrolled and renders both
  // views inline exactly as before (byte-identical).
  const controlled = !inReport && openEndTab !== undefined
  if (r.monadic && r.per_item && r.per_item.length > 0) {
    return (
      <div className="w-full flex flex-col gap-3" data-testid="qs-monadic-open-end">
        <MonadicOpenEndCard
          monadicThemes={r.monadic_themes ?? []}
          perItem={r.per_item}
          question={q}
          personas={personas}
          comparisonLabels={comparisonLabels}
          construct={controlled ? undefined : r.title}
          documentMode={documentMode}
          themesAsCarousel={!documentMode && inReport}
          hideQuotes={!documentMode && inReport}
          activeTab={controlled ? openEndTab : undefined}
          onTabChange={controlled ? onOpenEndTabChange : undefined}
          themesView={themesView}
          monadicThemePlot={r.monadic_theme_plot}
          quotePlot={r.quote_plot}
          monadicThemeHeatPlot={r.monadic_theme_heat_plot}
        />
        {/* Monadic methodology note — inline only on the UNCONTROLLED chat path (direct PlotRenderer
            usage / preview). The report card states its base via the per-card coverage footer, so it
            never shows here (`inReport`). On the CONTROLLED chat surface the OE card converges into
            PlotCard, which renders this same note (via `monadicCoverageNote`) in its `footnote` slot —
            so the inline copy is suppressed there to avoid a double render. */}
        {!inReport && !controlled && (
          <p className="text-xs text-twin-chat-fg-muted" data-testid="qs-monadic-note">
            {monadicCoverageNote(r)}
          </p>
        )}
      </div>
    )
  }
  return (
    <QuickStudyOpenEndCard
      themes={r.themes}
      responses={openEndQuoteResponses ?? resp}
      question={q}
      personas={personas}
      populationId={populationId}
      onJumpToSource={documentMode ? undefined : onJumpToSource}
      comparisonLabels={comparisonLabels}
      documentMode={documentMode}
      activeTab={controlled ? openEndTab : undefined}
      onTabChange={controlled ? onOpenEndTabChange : undefined}
      filteredEmpty={openEndQuoteFilteredEmpty}
      versionLabelById={buildMonadicVersionLabelMap(r)}
      themePlot={r.theme_plot}
      quotePlot={r.quote_plot}
    />
  )
}

// QA #11 — the inferred page titles from the ingest read-back (CreativeRead.title), in
// attachment order. Provided by QuickStudyPage per insight message (the reads that
// preceded that insight in the thread); default [] keeps every other mount (reports,
// tests, storybook-style usage) title-free with the plain item labels.
export const CreativeTitlesContext = createContext<string[]>([])

/**
 * NPS has an existing exhibit (`nps_diverging` → NpsDivergingBar). Likert
 * `diverging_stacked` and mean `rating` bars have no NPS end renderer — route
 * those ids into it. `grouped_bar` is the buckets alternate; pass
 * `preferCanonical` on the card default so a twin-test / registry fall-through
 * to grouped_bar still lands on the diverging NPS bar unless the user picks buckets.
 */
export function resolveNpsAwarePlot(plot: string, metric: string, preferCanonical = false): string {
  if (metric !== 'nps') {
    return plot
  }
  if (plot === 'diverging_stacked' || plot === 'rating') {
    return 'nps_diverging'
  }
  if (preferCanonical && plot === 'grouped_bar') {
    return 'nps_diverging'
  }
  return plot
}

// W6-QS-c: the result fields a cut band's payloads replace; cleared first so a band never
// shows the whole run's plot for a field its own cut did not draw.
const CLEARED_STUDY_PLOTS: Partial<Result> = {
  choice_plot: null,
  density_histogram_plot: null,
  quadrant_plot: null,
  ranking_plot: null,
  diverging_likert_plot: null,
  heat_grid_plot: null,
  stacked_100_bars_plot: null,
  // W7 Q3: a monadic multi select's per-item split.
  grouped_bars_plot: null,
  scorecard_plot: null,
}

/** W7 L2: the plot ids whose cut bands draw a stamped stat plot (likert or grid) on a new read. */
// Keep in step with the `likert` / `heatGrid` cases of `drawStampedRead` below.
const STAMPED_STAT_CUT_PLOTS = new Set(['bar', 'rating', 'histogram', 'grouped_bar', 'matrix', 'heatmap', 'diverging_stacked'])

/** Whether a new read's cut bands draw the stamped stat plot for this plot id (the card then keeps the plot's stat words). */
export function cutsDrawStampedStats(result: Pick<Result, 'plot_inputs'>, plot: string | undefined): boolean {
  return Boolean(result.plot_inputs) && STAMPED_STAT_CUT_PLOTS.has(plot ?? '')
}

/** `field:kind` of every study plot a read carries, so a cut band can be checked against its anchor. */
function stampedKinds(read: Result): Array<string> {
  return (Object.keys(CLEARED_STUDY_PLOTS) as Array<keyof Result>)
    .map((field) => {
      const plot = read[field] as { kind?: string } | null | undefined
      return plot?.kind ? `${String(field)}:${plot.kind}` : null
    })
    .filter((entry): entry is string => entry !== null)
}

// Plot ids whose cut bands toggle one cut at a time (their legacy cut readers do too).
const TOGGLE_CUT_PLOTS = new Set<string>(['quadrant', 'matrix', 'heatmap', 'radar', 'nps_diverging', 'grouped_bar'])

export function PlotRenderer({ result: r, selectedCuts, cutGroups, plotOverride, metricOverride, personas, populationId, onJumpToSource, intentView, audienceType, onDivergingChrome, openEndTab, onOpenEndTabChange, openEndQuoteResponses, openEndQuoteFilteredEmpty, contentLanguage, metricSwitch, rankingViewSwitch }: { result: Result; selectedCuts?: Array<string>; cutGroups?: Array<CompareSegmentsGroup>; plotOverride?: string; metricOverride?: string; personas?: QuickStudyPersona[]; /** Population/audience id the run's twins belong to — threaded to the plain-OE QuickStudyOpenEndCard's Quotes-view picker fallback. Optional, omitted preserves today's behavior. */ populationId?: string; onJumpToSource?: () => void; intentView?: boolean; audienceType?: string; onDivergingChrome?: (chrome: DivergingChromeData | null) => void; openEndTab?: 'themes' | 'quotes'; onOpenEndTabChange?: (t: 'themes' | 'quotes') => void; openEndQuoteResponses?: Array<TwinResponse>; openEndQuoteFilteredEmpty?: boolean; contentLanguage?: string; /** True when the card draws the metric switch: it then holds the stamped plots' stat view (one switch per choice). */ metricSwitch?: boolean; /** True when the card's View-as pills offer Ranking and First choice: they then pick a ranking's view (one switch per choice). */ rankingViewSwitch?: boolean }) {
  const colors = useMemo(() => getAppChartPalette(), [])
  const rankingContentLanguage = useContentLanguage(contentLanguage)
  const rankingLabels = useMemo(
    () => rankingChartLabelsForLanguage(rankingContentLanguage),
    [rankingContentLanguage],
  )
  // In-report rendering — threaded into renderOpenEndInsights so a many-topic monadic theme
  // card pages through a carousel anywhere in the report panel (Findings + Report), but stays
  // stacked in the chat. Uses ReportPlotContext (whole panel), not PlotCompactContext (which is
  // only the narrative view, so the Findings card the user sees would miss it).
  const inReport = useContext(ReportPlotContext)
  const comparisonLabels = useMemo(() => collectComparisonLabels(r), [r])
  const creativeTitles = useContext(CreativeTitlesContext)
  // Price-curves chrome and refusal copy in the VIEWER's UI language (plots plan G,
  // owner decision 5), not the run language Python stamped.
  const priceCurvesLabels = usePriceCurvesLabels()
  // Density-histogram chrome in the viewer's UI language (plots plan M, decision 5).
  const densityChrome = useDensityHistogramLabels()
  const densityPlot = r.density_histogram_plot?.kind === 'density-histogram' ? r.density_histogram_plot : null
  const densityHistogram = densityPlot ? (
    <div data-testid="qs-density-histogram">
      <DensityHistogramPlot
        data={{ ...densityPlot.data, labels: densityChrome.labels }}
        props={{ size: 'report', fit: 'card', locale: densityChrome.locale }}
        testIdPrefix="qs-density-histogram-plot"
      />
    </div>
  ) : null
  const priceCurveRefusalText = usePriceCurveRefusalText(r.price_curve_refusal)
  // W6-QS: a rating read Python stamped as the study's `diverging-likert` plot, and a rating grid
  // as its `heat-grid`. One switch per choice: where the card draws the metric switch, its metric
  // is the plots' stat view and they draw no tabs of their own; elsewhere they open on the
  // payload's default view (Top N, or NPS) unless the metric was switched. Every rating plot id
  // draws them when no cut is selected; a selected cut draws only the legacy cut view.
  const metricView = likertViewForMetric(metricOverride || r.metric)
  // The card's switch holds the view only when the stamped plot offers it (else the plot keeps
  // its own tabs, or Average only): a click is never silently dead.
  const statViewControlled = Boolean(metricSwitch) && stampedPlotOffers(r, metricView)
  const likertView = statViewControlled
    ? metricView
    : metricOverride && metricOverride !== r.metric
      ? likertViewForMetric(metricOverride)
      : null
  const likertPlot = hasQuickStudyLikertPlot(r) ? (
    <Sized grow>
      <QuickStudyLikertPlot
        key={statViewControlled ? 'controlled' : likertView ?? 'default'}
        result={r}
        view={likertView}
        controlled={statViewControlled}
        showBase={!intentView}
      />
    </Sized>
  ) : null
  // W7 Q11: a read's stamped likert plot (the whole run, or one cut band from the cut plots),
  // for the rating card's own Compare-segments picker. Null when the read carries none.
  const drawLikertRead = (read: Result): ReactNode | null => {
    if (!hasQuickStudyLikertPlot(read)) {
      return null
    }
    const readControlled = Boolean(metricSwitch) && stampedPlotOffers(read, metricView)
    const readLikertView = readControlled ? metricView : likertView
    return (
      <QuickStudyLikertPlot
        key={readControlled ? 'controlled' : readLikertView ?? 'default'}
        result={read}
        view={readLikertView}
        controlled={readControlled}
        showBase={!intentView}
      />
    )
  }
  // The quadrant's "shown of total" note, also in the viewer's language (plots plan O).
  const { t } = useTranslation('app')
  // A monadic significance test reads Top 2 box; say so where the plot draws another Top N
  // (Top 3 on 10 and 11 points) or none (0-100). The box is named in the plot's own words.
  const likertBoxSize = r.diverging_likert_plot?.data.rows?.[0]?.stats?.boxSize
  const likertTopNTemplate = r.diverging_likert_plot?.data.labels?.stat_tab_top_n
  const testedBoxNote =
    hasQuickStudyLikertPlot(r) && likertBoxSize !== 2
      ? t('studyReport.sigTestedOnBox', {
          box: likertTopNTemplate ? likertTopNTemplate.replace('{n}', '2') : t('studyReport.statTabTopN', { n: 2 }),
        })
      : undefined
  // The monadic scorecard (LP A/B grid): SC draws Python's `scorecard_plot` (the shared heat grid,
  // its radar alternative); a scorecard stored without it keeps its legacy views.
  const isScorecard = Boolean(r.monadic && (r.monadic_scorecard?.length ?? 0) > 0)
  // Whether a read (the whole run, or one cut band) draws through the stamped grid. A scorecard
  // reads only its own `scorecard_plot`: a band without one falls back to legacy, never to the
  // pooled `heat_grid_plot` (SC-f2).
  const isGridReadable = (read: Result): boolean =>
    isScorecard ? hasQuickStudyScorecardPlot(read) : hasQuickStudyGridPlot(read)
  const stampedGrid = (
    <QuickStudyGridPlot result={r} view="heat-grid" statView={metricView} controlled={statViewControlled} />
  )
  const drawScorecardPlot = (read: Result): ReactNode | null =>
    hasQuickStudyScorecardPlot(read) ? (
      <QuickStudyGridPlot result={read} view="heat-grid" statView={metricView} controlled={statViewControlled} />
    ) : null
  const quadrantShownOfTotal = t('studyReport.quadrantShownOfTotal')
  const { question: q, responses: resp } = useMemo(() => {
    // F7a — fold rejecter ("None of the above") picks back into the tally before any
    // boolean re-labelling, so the None slice reflects the real share, not 0.
    const reconciled = reconcileNoneSlice(r.question, r.responses, r.option_metadata)
    return yesNoize(reconciled.question, reconciled.responses)
  }, [r.question, r.responses, r.option_metadata])

  const resolvedMetric = metricOverride || r.metric || ''
  const requestedPlot = plotOverride || r.plot
  const activePlot = resolveNpsAwarePlot(requestedPlot, resolvedMetric, !plotOverride)
  // W7 M3: when the card's View-as pills offer both ranking views, a ranking draws the view they
  // picked (Ranking, or First choice on `bar`) with no second in-plot toggle.
  const rankingViewControlled = Boolean(rankingViewSwitch)
  // Monadic OE themes chart-type: the MessageRow switch stores the choice as a synthetic
  // plotOverride; on report surfaces the ledger bakes it into the result's `plot` (no
  // plotOverride passed), so the derivation must read both.
  const themesView: 'heatmap' | 'bars' = (plotOverride || r.plot) === 'themes_bars' ? 'bars' : 'heatmap'
  // Wave 4 — diverging/scorecard convergence. The chat `InsightCard` passes an
  // `onDivergingChrome` sink (and `intentView`); those exhibits then render CHROME-LESS
  // (`'none'`) and publish their subtitle/footnote/picker up so the outer `PlotCard` owns
  // them. The report/preview surface passes neither → `'self'` → byte-identical chrome.
  const divergingChromeMode: 'self' | 'none' = intentView && onDivergingChrome ? 'none' : 'self'
  // Compare-segments (the shared host): the picker upstream hands down the selected cut ids
  // (attribute::value demographic cuts + authored subsegment labels) and the groups that
  // labelled them. Re-slicing is client-side off raw responses, so extending it to a plot
  // is render work, not new data. `cutSlices` are the selected cuts; every renderer prepends
  // the always-present "Everyone" anchor itself (the Mode A idiom, matching the diverging card).
  const cutLabelFor = useMemo(() => {
    const map = new Map<string, string>()
    // Band headers / chips render OUTSIDE the grouped picker menu, so an ambiguous bare
    // value ("1", "2-4", "High") gets its group label prefixed ("Tech comfort: 1").
    for (const g of cutGroups ?? []) for (const o of g.options) map.set(o.id, qualifiedCutDisplayLabel(g.groupLabel, o.label))
    return (cid: string): string | undefined => map.get(cid)
  }, [cutGroups])
  const cutSlices = useMemo<Array<CutIdSlice<TwinResponse>>>(
    () => sliceResponsesByCutIds(resp, selectedCuts ?? [], cutLabelFor),
    [resp, selectedCuts, cutLabelFor],
  )
  const hasCuts = cutSlices.length > 0
  // Mode height keys off cuts that will actually chart (R1.1). Thin/suppressed
  // slices still keep hasCuts so the note path runs, but they must not flip
  // rows→toggle on their own.
  const chartableCutCount = cutSlices.filter((s) => !s.suppressed).length
  // Per-plot mode (Step 2): 'rows' (Mode A, stacked bands under the anchor) while compact,
  // 'toggle' (Mode B, one cut at a time) once the anchored small multiples would run too tall.
  const segMode = (rowCount: number): 'rows' | 'toggle' => segmentedMode(activePlot, chartableCutCount, rowCount)
  // W6-QS-c: with cuts picked, the read's study plots per cut, drawn by Python on demand
  // (`/workflow-runs/:token/cut-plots`). A stored read (no `plot_inputs`), a refusal or a
  // failure leaves `cutPlots` unusable and the legacy cut readers below draw as today.
  const cutPlotRequest = useMemo(
    () => cutSlices.map((slice) => ({ id: slice.cutId, label: slice.label })),
    [cutSlices],
  )
  const cutPlots = useQuickStudyCutPlots(r, cutPlotRequest)

  // Step 10 — server spec is the numeric authority for the default exhibit.
  // Interactive cuts stay on today's client-row aggregation (spec-per-cut is follow-up).
  // W7 Q1: a stamped study plot wins over a plotless spec (the spec would draw browser bands);
  // specs that each carry Python's plot draw it. A stored spec-only read keeps the spec path.
  const findingAnalystSpecs = analystSpecsFromResult(r)
  if (
    shouldPreferAnalystSpec(findingAnalystSpecs, selectedCuts) &&
    (stampedKinds(r).length === 0 || findingSpecsCarryPlots(findingAnalystSpecs))
  ) {
    const parity = reportFindingPlotParity(r, findingAnalystSpecs)
    return (
      <FindingServerSpecPlot
        charts={findingAnalystSpecs.charts}
        tables={findingAnalystSpecs.tables}
        hasParityMismatch={Boolean(parity && parity.compared > 0 && !parity.ok)}
      />
    )
  }

  // A rerouted/programmatic selection (a `segment_by` default or a "break down by X" chip)
  // can land on a chart that can't be sliced yet (pricing). The picker is hidden there, so
  // this is the only surface for the "not available yet" note.
  if (hasCuts && !supportsSegments(activePlot)) {
    return <SegmentsUnavailableNote />
  }

  // W6-QS-c: the study plot this plot id draws for a read (the whole run, or one cut's
  // payloads substituted in), through the component and the rules the no-cut path draws it
  // with. Null when the read carries no such plot: the caller then keeps the legacy cut reader.
  const drawStampedRead = (read: Result): ReactNode | null => {
    const readControlled = Boolean(metricSwitch) && stampedPlotOffers(read, metricView)
    const readLikertView = readControlled ? metricView : likertView
    const likert = hasQuickStudyLikertPlot(read) ? (
      <QuickStudyLikertPlot
        key={readControlled ? 'controlled' : readLikertView ?? 'default'}
        result={read}
        view={readLikertView}
        controlled={readControlled}
        showBase={!intentView}
      />
    ) : null
    const heatGrid =
      isGridReadable(read) ? (
        <QuickStudyGridPlot result={read} view="heat-grid" statView={metricView} controlled={readControlled} />
      ) : null
    const choice =
      !read.monadic && isChoiceQuestion(q) && hasQuickStudyChoicePlot(read) ? <QuickStudyChoicePlot result={read} /> : null
    // W7 Q2 / Q3: a monadic yes / no or choice read's per-item plot (never the pooled split).
    const monadicItems = hasQuickStudyMonadicPlot(read) ? <QuickStudyMonadicPlot result={read} /> : null
    // W7 Q5 / Q6: the ranking read, with its first-choice view when Python stamped one.
    const rankingRead = (initialView: QuickStudyRankingView): ReactNode | null =>
      quickStudyRankingAdapter.accepts(read) ? (
        <QuickStudyRankingPlot result={read} initialView={initialView} controlled={rankingViewControlled} />
      ) : null
    const density =
      read.density_histogram_plot?.kind === 'density-histogram' ? (
        <div data-testid="qs-density-histogram">
          <DensityHistogramPlot
            data={{ ...read.density_histogram_plot.data, labels: densityChrome.labels }}
            props={{ size: 'report', fit: 'card', locale: densityChrome.locale }}
            testIdPrefix="qs-density-histogram-plot"
          />
        </div>
      ) : null
    switch (activePlot) {
      case 'ranking':
        return rankingRead('ranking')
      case 'pie':
        return monadicItems ?? choice
      case 'bar':
        if (likert) return likert
        if (monadicItems) return monadicItems
        if (q.type === 'ranking') return rankingRead(intentView || rankingViewControlled ? 'first-choice' : 'ranking')
        // A monadic yes / no or choice read stored before its per-item plot keeps its legacy bars.
        if (read.monadic && (read.monadic_boolean?.length || read.monadic_choice?.length)) return null
        return heatGrid ?? choice
      case 'rating':
      case 'histogram':
        return likert ?? density
      case 'nps_diverging':
        // W7 Q7: the NPS read when Python stamped one, else the read's likert plot (never the browser NPS bar).
        return likert
      case 'grouped_bar':
        // A rating: the NPS split when Python stamped one, else the likert read (the no-cut order).
        if (q.type === 'rating') {
          return !read.monadic && hasQuickStudyChoicePlot(read) ? <QuickStudyChoicePlot result={read} /> : likert
        }
        if (q.type === 'text') return density
        // W7 Q6: a ranking's grouped bars are its stamped ranking read.
        if (q.type === 'ranking') return rankingRead('ranking')
        return choice
      case 'matrix':
      case 'heatmap':
        return heatGrid
      case 'radar':
        return isGridReadable(read) ? <QuickStudyGridPlot result={read} view="radar" /> : null
      case 'quadrant': {
        if (!quadrantQuickStudyAdapter.accepts(read)) return heatGrid
        const { data } = quadrantQuickStudyAdapter.read(read)
        return (
          <div data-testid="qs-quadrant" className="h-[420px]">
            <QuadrantPlot data={data} props={{ colorMode: 'byItem' }} shownOfTotalTemplate={quadrantShownOfTotal} testId="qs-quadrant-plot" />
          </div>
        )
      }
      case 'diverging_stacked':
        // The converged rating / scorecard exhibits own their in-exhibit picker (legacy for now).
        if (Array.isArray((q as any)?.columns) && (q as any).columns.length > 0) {
          return isGridReadable(read) ? (
            <QuickStudyGridPlot result={read} view="heat-grid" statView={metricView} controlled={readControlled} />
          ) : null
        }
        if (q.type === 'rating') return null
        return density
      default:
        return isOpenEndQuestion(q) ? null : choice
    }
  }
  // A new read (`plot_inputs`) never draws browser bands, even with no cut-plots source (W7 M1).
  const isCutPlotsRead = cutPlots.status !== 'unavailable' || Boolean(r.plot_inputs)
  if (hasCuts && isCutPlotsRead && drawStampedRead(r) !== null) {
    // A skeleton only while a request is really in flight (a resolved pick reads synchronously).
    if (cutPlots.status === 'loading') {
      return (
        <Sized grow>
          <div data-testid="qs-cut-plots-loading">
            <ChartSkeleton />
          </div>
        </Sized>
      )
    }
    const question = cutPlots.question
    if (cutPlots.status === 'ready' && question?.status === 'ok') {
      const bandPlots = question.plots ?? {}
      // A cut band reads only its own payloads: the whole run's are cleared first.
      const readFor = (cutId: string): Result =>
        cutId === '__all__' ? r : { ...r, ...CLEARED_STUDY_PLOTS, ...(bandPlots[`cut:${cutId}`] ?? {}) }
      const anchorKinds = stampedKinds(r)
      // Every drawn band must carry its own payload, of the kinds the Everyone band draws:
      // a band that would read differently from its anchor sends the card to the legacy view.
      const isDrawable = cutSlices
        .filter((slice) => !slice.suppressed)
        .every((slice) => {
          const read = readFor(slice.cutId)
          return (
            bandPlots[`cut:${slice.cutId}`] != null &&
            drawStampedRead(read) !== null &&
            stampedKinds(read).every((entry) => anchorKinds.includes(entry))
          )
        })
      if (isDrawable) {
        const rowCount = q.choices?.length ?? r.monadic_rating?.length ?? 2
        return (
          <Sized grow>
            <div data-testid="qs-cut-plots">
              <SegmentBandBlocks
                anchorResponses={resp}
                cutSlices={cutSlices}
                mode={TOGGLE_CUT_PLOTS.has(activePlot) ? 'toggle' : segMode(rowCount)}
                renderBand={(_rows, cutId) => drawStampedRead(readFor(cutId))}
              />
            </div>
          </Sized>
        )
      }
    }
    // W7 Q10: a new read whose cut plots failed, were refused or cannot be drawn says so
    // (a retry only when the request failed); it never falls back to browser bands.
    return (
      <Sized grow>
        <CutPlotsError onRetry={cutPlots.retry} />
      </Sized>
    )
  }

  // Plots plan I: a new ranking insight carries Python's `ranking-bars` plot (the one
  // weighted-score rule, dense ranks, the stamped winner and scale) and draws it; a
  // stored result without it keeps the browser RankingChart (legacy reader).
  const renderRankingTotal = (initialView: QuickStudyRankingView = 'ranking'): ReactNode => {
    if (quickStudyRankingAdapter.accepts(r)) {
      // W7 Q5: with the first-choice view (Python's `first_choices`) when stamped.
      return (
        <Sized grow>
          <QuickStudyRankingPlot result={r} initialView={initialView} controlled={rankingViewControlled} />
        </Sized>
      )
    }
    return (
      <Sized grow>
        <RankingChart question={q} responses={resp} colors={colors} labels={rankingLabels} />
      </Sized>
    )
  }

  // QS-32: a valid alternate representation chosen via the switcher overrides the
  // backend's default plot (render-only — same responses).
  switch (activePlot) {
    case 'migration_matrix':
      return (
        <Sized grow>
          <MigrationMatrixChart
            rows={(r.migration ?? []).map((row) => ({
              ...row,
              anchor_price: row.anchor_price ?? null,
            }))}
          />
        </Sized>
      )
    case 'ranking':
      // Weighted RankingChart (higher score = more preferred) is the ranking exhibit.
      // #17 (J15) — segmented ranking renders band-ordered small multiples of the pooled
      // chart (Everyone anchor + n= per band, single accent hue, thin-cell caveat) instead
      // of deck-ui's size-ordered rainbow view.
      if (hasCuts) {
        return (
          <Sized grow>
            <SegmentBandBlocks
              anchorResponses={resp}
              cutSlices={cutSlices}
              mode={segMode(q.choices?.length ?? 2)}
              renderBand={(band) => <RankingChart question={q} responses={band} colors={colors} labels={rankingLabels} />}
            />
          </Sized>
        )
      }
      return renderRankingTotal()
    case 'pie':
      // #17 (J15) — segmented pie: per-band blocks of the LIST layout (deck-ui's
      // segmented pie already fell back to a list — per-band doughnuts don't compare).
      if (hasCuts) {
        return (
          <Sized grow>
            <SegmentBandBlocks
              anchorResponses={resp}
              cutSlices={cutSlices}
              mode={segMode(q.choices?.length ?? 2)}
              renderBand={(band) => (
                <SingleChoiceChart question={q} responses={band} type="bar" colors={colors} includeEmptyChoices />
              )}
            />
          </Sized>
        )
      }
      // W7 Q2 / Q3: a monadic yes / no or choice read draws its per-item plot, never a pooled pie.
      if (hasQuickStudyMonadicPlot(r)) {
        return (
          <Sized grow>
            <QuickStudyMonadicPlot result={r} />
          </Sized>
        )
      }
      // Plots plan J: a choice read Python stamped draws the canonical plot, on its donut.
      if (!r.monadic && isChoiceQuestion(q) && hasQuickStudyChoicePlot(r)) {
        return (
          <Sized grow>
            <QuickStudyChoicePlot result={r} initialMode="share-donut" />
          </Sized>
        )
      }
      // Taller so the doughnut + a multi-row colour legend (e.g. the 9-emotion
      // lexicon) fit without bleeding into the card footer.
      return (
        <Sized h={Math.max(340, 300 + (q.choices?.length ?? 2) * 8)}>
          <SingleChoiceChart question={q} responses={resp} type="pie" colors={colors} includeEmptyChoices />
        </Sized>
      )
    case 'bar':
      // W6-QS: a rating read Python stamped (pooled, or one row per monadic item).
      if (likertPlot && !hasCuts) {
        return likertPlot
      }
      // W7 Q2 / Q3: a monadic yes / no or choice read Python stamped per item.
      if (!hasCuts && hasQuickStudyMonadicPlot(r)) {
        return (
          <Sized grow>
            <QuickStudyMonadicPlot result={r} />
          </Sized>
        )
      }
      // Monadic boolean: per-item yes-rate bar — one bar per item sorted by yes%.
      if (r.monadic && r.monadic_boolean && r.monadic_boolean.length > 0) {
        const data = buildMonadicBooleanBar(r.monadic_boolean)
        // Chat (`intentView`): DOM TrackBar list (yes-rate % is a per-row magnitude, no
        // x-axis needed). Report/deck: keep the canvas HBar.
        if (intentView) {
          return (
            <Sized grow>
              <div data-testid="qs-monadic-boolean-bar">
                <MagnitudeBarList data={data} color={colors[0]} />
              </div>
            </Sized>
          )
        }
        return (
          <Sized grow>
            <div data-testid="qs-monadic-boolean-bar">
              <HBar
                data={data}
                colors={colors}
                autoHeight
                xAxisTitle="Yes rate (%)"
                xAxisMax={100}
              />
            </div>
          </Sized>
        )
      }
      // QSB-4 — Monadic rating: the Distribution toggle must show one distribution PER
      // creative. Falling through to the pooled scalar path merged every creative's
      // answers into one histogram (a loved 4.4 and a hated 1.8 read as one "mixed"
      // 3.1); the per-creative distributions already ship on monadic_rating[].distribution.
      if (r.monadic && r.monadic_rating && r.monadic_rating.length > 0) {
        return (
          <Sized grow>
            <div className="space-y-5" data-testid="qs-monadic-rating-distribution">
              {r.monadic_rating.map((item) => {
                const intent = scalarIntentFromMonadicItem(item, q)
                if (!intent) return null
                return (
                  <div key={item.id} className="space-y-1.5">
                    <div className="text-sm font-medium text-twin-chat-fg">
                      {item.label} <span className="text-twin-chat-fg-muted">(n={item.n})</span>
                    </div>
                    <ScalarIntentChart data={intent} view="distribution" />
                  </div>
                )
              })}
            </div>
          </Sized>
        )
      }
      // Monadic radiogroup/checkbox: per-item choice distribution — one bar per item
      // showing the top-choice select-rate (a quick cross-item scan; the tooltip details).
      if (r.monadic && r.monadic_choice && r.monadic_choice.length > 0) {
        const data = buildMonadicChoiceBar(r.monadic_choice)
        // Chat (`intentView`): DOM TrackBar list (select-rate % is a per-row magnitude, no
        // x-axis needed). Report/deck: keep the canvas HBar.
        if (intentView) {
          return (
            <Sized grow>
              <div data-testid="qs-monadic-choice-bar">
                <MagnitudeBarList data={data} color={colors[0]} />
              </div>
            </Sized>
          )
        }
        return (
          <Sized grow>
            <div data-testid="qs-monadic-choice-bar">
              <HBar
                data={data}
                colors={colors}
                autoHeight
                xAxisTitle="Top choice select rate (%)"
                xAxisMax={100}
              />
            </div>
          </Sized>
        )
      }
      // A ranking answer is an ORDERING ARRAY. The 'ranking' view shows the averaged
      // ORDER (RankingChart); in the chat InsightCard (`intentView`) this 'bar'/
      // distribution toggle must show a GENUINELY different cut — the share-of-#1-vote
      // per option (% of twins ranking each item first) — so Ranking↔Distribution is no
      // longer a pixel-identical no-op. The report/deck path (no intentView) keeps the
      // weighted RankingChart so the 'bar' export still matches the headline winner.
      if (q.type === 'ranking') {
        // W7 Q5: a stamped ranking opens on its stamped first-choice view in the chat card.
        if (!hasCuts && quickStudyRankingAdapter.accepts(r)) {
          return renderRankingTotal(intentView || rankingViewControlled ? 'first-choice' : 'ranking')
        }
        const firstChoice = intentView ? buildRankingFirstChoice(q, resp) : null
        if (firstChoice && firstChoice.length > 0) {
          // Chat-only (`firstChoice` is built ONLY under `intentView`): the share-of-#1
          // vote is a per-row 0–100% magnitude → DOM TrackBar list, no x-axis needed.
          return (
            <Sized grow>
              <div data-testid="qs-ranking-first-choice-bar">
                <MagnitudeBarList data={firstChoice} color={colors[0]} />
              </div>
            </Sized>
          )
        }
        // #17 (J15) — banded small multiples for the segmented cut (see case 'ranking').
        if (hasCuts) {
          return (
            <Sized grow>
              <SegmentBandBlocks
                anchorResponses={resp}
                cutSlices={cutSlices}
                mode={segMode(q.choices?.length ?? 2)}
                renderBand={(band) => <RankingChart question={q} responses={band} colors={colors} labels={rankingLabels} />}
              />
            </Sized>
          )
        }
        return renderRankingTotal()
      }
      // W6-QS-b: a grid Python stamped (one row per option, its mean and Top N views).
      if (!hasCuts && isGridReadable(r)) {
        return <Sized grow>{stampedGrid}</Sized>
      }
      // QS — a SINGLE-metric matrixdropdown (one rating column scored across N
      // rows/creatives) is NOT a choice tally: its answer is { rowId: { colId: rating } }.
      // SingleChoiceChart would count occurrences (wrong shape) → render one bar per row
      // where each bar is that row's MEAN rating. Multi-column matrixdropdowns return null
      // here and fall through to the matrix/heatmap path that owns them.
      {
        // The render-only metric switch (mean ⇄ top-2-box %) resolves here: an explicit
        // override wins, else the result's own metric, else mean. Only 'top_2_box' departs
        // from the default per-row mean.
        const resolvedMetric = metricOverride || r.metric || ''
        const isTop2 = resolvedMetric === 'top_2_box'
        // Top-2-box reads as a DIVERGING stacked bar per row (bottom-2 / neutral / top-2)
        // so the downside shows, not just the headline top-2 — the standard Likert view.
        if (isTop2) {
          const diverging = buildMatrixDiverging(q, resp)
          if (diverging) {
            // Pure DOM list — content-sized (`grow`); the old fixed height over-reserved
            // (~90px of dead space between the legend and the card footnote on 3 rows).
            return (
              <Sized grow>
                <MatrixDivergingBars rows={diverging} />
              </Sized>
            )
          }
        }
        const matrixBar = buildMatrixBar(q, resp, isTop2 ? 'top_2_box' : 'mean')
        if (matrixBar) {
          const col0 = (q.columns ?? [])[0] ?? {}
          const scaleMin = typeof col0.rateMin === 'number' ? col0.rateMin : 1
          const scaleMax = typeof col0.rateMax === 'number' ? col0.rateMax : 5
          // Top-2-box is a 0–100% read; mean keeps the question's own score scale.
          const xAxisTitle = isTop2 ? 'Top-2-box %' : `Score (${scaleMin}–${scaleMax})`
          const xAxisMax = isTop2 ? 100 : scaleMax
          return (
            <Sized grow>
              <div data-testid="qs-matrix-bar">
                <HBar
                  data={matrixBar}
                  colors={colors}
                  autoHeight
                  xAxisTitle={xAxisTitle}
                  xAxisMax={xAxisMax}
                />
              </div>
            </Sized>
          )
        }
      }
      // Single-question rating shown as a distribution → the intent-coloured N-point
      // breakdown (top-2 green / neutral grey / bottom-2 red), not a flat choice tally.
      // Gated to the chat InsightCard (`intentView`); the report keeps SingleChoiceChart.
      if (intentView) {
        const intent = buildScalarIntent(q, resp)
        if (intent) {
          if (hasCuts) {
            return (
              <Sized grow>
                <SegmentedScalarIntent
                  question={q}
                  anchorResponses={resp}
                  cutSlices={cutSlices}
                  metric={(metricOverride || r.metric) === 'mean' ? 'mean' : 'top_2_box'}
                  colors={colors}
                  view="distribution"
                />
              </Sized>
            )
          }
          return (
            <Sized grow>
              <ScalarIntentChart data={intent} view="distribution" />
            </Sized>
          )
        }
      }
      if (isOpenEndQuestion(q)) {
        return renderOpenEndInsights(r, q, resp, personas, comparisonLabels, populationId, onJumpToSource, creativeTitles, inReport, openEndTab, onOpenEndTabChange, openEndQuoteResponses, openEndQuoteFilteredEmpty, themesView)
      }
      // #17 (J15) — segmented single-select: band-ordered blocks of the pooled list
      // (Everyone anchor + n= per band, single accent hue, thin-cell caveat) instead of
      // deck-ui's size-ordered rainbow grouping.
      if (hasCuts) {
        return (
          <Sized grow>
            <SegmentBandBlocks
              anchorResponses={resp}
              cutSlices={cutSlices}
              mode={segMode(q.choices?.length ?? 2)}
              renderBand={(band) => (
                <SingleChoiceChart question={q} responses={band} type="bar" colors={colors} includeEmptyChoices />
              )}
            />
          </Sized>
        )
      }
      // Plots plan J: a choice read Python stamped draws the canonical plot.
      if (!r.monadic && isChoiceQuestion(q) && hasQuickStudyChoicePlot(r)) {
        return (
          <Sized grow>
            <QuickStudyChoicePlot result={r} />
          </Sized>
        )
      }
      return (
        <Sized grow>
          <SingleChoiceChart question={q} responses={resp} type="bar" colors={colors} includeEmptyChoices />
        </Sized>
      )
    case 'diverging_stacked': {
      // R8 — the default exhibit for NEW rating results (python emits the plot id; stored
      // runs keep their old plot ids and the legacy branches below stay intact — that
      // payload-driven split IS the rollout mechanism). One centered diverging row per
      // item with always-visible row stats and the Compare-segments picker as the card's
      // single control; the per-point histogram drill-in lives in the Report tab.
      // Scorecard matrix (Step 6 / R2): a monadic matrixdropdown scorecard grid ships
      // per-dimension × per-item aggregates. The exhibit is the mean-anchored HEATMAP
      // (dimensions × items) — scale-agnostic and discriminating where stacked diverging
      // rows collapsed on a 1–10 run.
      // Authored subsegments (Agent F) prepend as the "Audience segments" group ahead of the
      // derived demographic groups — same CompareSegmentsGroup shape, safe [] when absent.
      const authoredGroups = buildAuthoredSegmentGroups(r.segment_options)
      if (r.monadic && r.monadic_scorecard && r.monadic_scorecard.length > 0) {
        return (
          <Sized grow>
            <ScorecardHeatmap result={r} question={q} audienceType={audienceType} extraSegmentGroups={authoredGroups} chrome={divergingChromeMode} onChrome={onDivergingChrome} drawPlot={drawScorecardPlot} />
          </Sized>
        )
      }
      if (r.monadic && r.monadic_rating && r.monadic_rating.length > 0) {
        return (
          <Sized grow>
            <DivergingStackedRating result={r} question={q} audienceType={audienceType} extraSegmentGroups={authoredGroups} chrome={divergingChromeMode} onChrome={onDivergingChrome} plotExhibit={likertPlot ?? undefined} testedBoxNote={testedBoxNote} drawPlot={drawLikertRead} />
          </Sized>
        )
      }
      // Single-scalar rating: KEEP the existing summary (T2B hero + mean + sentiment bar)
      // as the exhibit, wrapped in the shared chrome (with the picker adding cut rows).
      // W6-QS: with no cut selected the exhibit is the stamped plot when Python drew one; a
      // selected cut draws only the legacy cut view (the summary, Everyone and the cut rows).
      {
        const intent = buildScalarIntent(q, resp)
        const legacyScalar = intent ? <ScalarIntentChart data={intent} view="summary" /> : null
        if (legacyScalar) {
          return (
            <Sized grow>
              <DivergingStackedRating
                result={r}
                question={q}
                audienceType={audienceType}
                scalarExhibit={legacyScalar}
                plotExhibit={likertPlot ?? undefined}
                extraSegmentGroups={authoredGroups}
                chrome={divergingChromeMode}
                onChrome={onDivergingChrome}
                drawPlot={drawLikertRead}
              />
            </Sized>
          )
        }
        // No legacy summary to fall back on: the stamped plot alone with no cut; with a cut, the
        // legacy path below.
        if (likertPlot && !hasCuts) {
          return likertPlot
        }
      }
      // Defensive: a diverging_stacked payload that is MATRIX-shaped but carries no monadic
      // scorecard/rating series (a non-monadic single-metric grid, e.g. solution_fit/gabor whose
      // default flipped to diverging_stacked) renders via the matrix score grid, not the numeric
      // chart — never a degraded/empty card. These fallback shapes are segmentable through the
      // generic seam (the converged exhibits own their published picker instead), so honor cuts
      // here exactly like the sibling matrix/numeric cases — never a dead picker.
      if (Array.isArray((q as any)?.columns) && (q as any).columns.length > 0) {
        // W6-QS-b: the grid Python stamped.
        if (!hasCuts && hasQuickStudyGridPlot(r)) {
          return <Sized grow>{stampedGrid}</Sized>
        }
        if (hasCuts) {
          return (
            <SegmentBandBlocks
              anchorResponses={resp}
              cutSlices={cutSlices}
              mode="toggle"
              renderBand={(band) => (
                <Sized grow>
                  <MatrixChart question={q} responses={band} colors={colors} />
                </Sized>
              )}
            />
          )
        }
        return (
          <Sized grow>
            <MatrixChart question={q} responses={resp} colors={colors} />
          </Sized>
        )
      }
      // Otherwise (a non-rating scalar): the Python histogram when stamped (plots plan M).
      if (densityHistogram && !hasCuts) {
        return <Sized grow>{densityHistogram}</Sized>
      }
      // Stored results fall back to the shared numeric chart.
      if (hasCuts) {
        return (
          <Sized grow>
            <SegmentBandBlocks
              anchorResponses={resp}
              cutSlices={cutSlices}
              mode={segMode(q.choices?.length ?? 2)}
              renderBand={(band) => (
                <NumericChart question={q} responses={band} colors={colors} />
              )}
            />
          </Sized>
        )
      }
      return (
        <Sized grow>
          <NumericChart question={q} responses={resp} colors={colors} />
        </Sized>
      )
    }
    case 'rating':
    case 'histogram':
      // W6-QS: a rating read Python stamped (pooled, or one row per monadic item); the plot's
      // own Average | Top N tabs replace the mean and distribution bars.
      if (likertPlot && !hasCuts) {
        return likertPlot
      }
      // Monadic rating: per-item ranked bar (mean or top-2-box depending on the metric
      // switcher), one bar per item sorted highest-first — the head-to-head visual.
      if (r.monadic && r.monadic_rating && r.monadic_rating.length > 0) {
        const resolvedMetric = metricOverride || r.metric || 'mean'
        const isTop2 = resolvedMetric === 'top_2_box'
        const scaleMax = r.monadic_rating[0]?.scale_max ?? 5
        // Compare segments — render per-cut small multiples from the per-item raw responses
        // (Everyone anchor + selected cuts). Falls through to the pooled bar when no cut
        // yields a scored slice. Mode A stacks; Mode B toggles once the anchored bands (cuts
        // + 1) × items would run too tall.
        // R1.1 — always enter when cuts are selected; the helper drops suppressed cuts,
        // names them, and falls back to the pooled bar when nothing chartable remains.
        if (hasCuts && r.per_item && r.per_item.length > 0) {
          return (
            <Sized grow>
              <SegmentedMonadicRatingBar
                result={r}
                question={q}
                cutSlices={cutSlices}
                mode={segMode(r.monadic_rating.length)}
                metric={isTop2 ? 'top_2_box' : 'mean'}
                colors={colors}
              />
            </Sized>
          )
        }
        const data = buildMonadicRatingBar(r.monadic_rating, resolvedMetric)
        const xAxisMax = isTop2 ? 100 : scaleMax
        const xAxisTitle = isTop2 ? 'Top-2-box %' : `Mean score (1–${scaleMax})`
        return (
          <Sized grow>
            <div data-testid="qs-monadic-rating-bar">
              <HBar
                data={data}
                colors={colors}
                autoHeight
                xAxisTitle={xAxisTitle}
                xAxisMax={xAxisMax}
              />
            </div>
          </Sized>
        )
      }
      // Monadic numeric: one histogram per item on the pooled edges (plots plan M).
      // Stored results without the plot keep the per-item median bar (legacy).
      if (r.monadic && r.monadic_numeric && r.monadic_numeric.length > 0 && densityHistogram && !hasCuts) {
        return <Sized grow>{densityHistogram}</Sized>
      }
      // Monadic numeric: per-item median bar.
      if (r.monadic && r.monadic_numeric && r.monadic_numeric.length > 0) {
        const data = buildMonadicNumericBar(r.monadic_numeric)
        return (
          <Sized grow>
            <div data-testid="qs-monadic-numeric-bar">
              <HBar
                data={data}
                colors={colors}
                autoHeight
                xAxisTitle="Median"
                xAxisMax={Math.max(...data.map((d) => d.value)) * 1.15 || 100}
              />
            </div>
          </Sized>
        )
      }
      // Single-question rating → the intent SUMMARY (Top-2-box hero + sentiment bar), not a
      // bare average gauge. Only Likert ratings; continuous/per-asset keep NumericChart.
      // Gated to the chat InsightCard (`intentView`) — the report/deck keep the deck-ui chart.
      // "View as: Histogram" renders the per-scale-point DISTRIBUTION of the same intent
      // read (the switch used to be a no-op — both options landed on the summary).
      if (intentView) {
        const intent = buildScalarIntent(q, resp)
        if (intent) {
          const intentChartView = activePlot === 'histogram' ? 'distribution' : 'summary'
          if (hasCuts) {
            return (
              <Sized grow>
                <SegmentedScalarIntent
                  question={q}
                  anchorResponses={resp}
                  cutSlices={cutSlices}
                  metric={(metricOverride || r.metric) === 'mean' ? 'mean' : 'top_2_box'}
                  colors={colors}
                  view={intentChartView}
                />
              </Sized>
            )
          }
          return (
            <Sized grow>
              <ScalarIntentChart data={intent} view={intentChartView} />
            </Sized>
          )
        }
      }
      // Numeric answers (plots plan M): the Python histogram, shared edges, no browser
      // binning. Cuts stay on the client-row chart until a figure is served per cut.
      if (densityHistogram && !hasCuts) {
        return <Sized grow>{densityHistogram}</Sized>
      }
      // #17 (J15) — segmented numeric: band-ordered blocks of the pooled chart
      // (Everyone anchor + n= per band, single accent hue, thin-cell caveat). NumericChart
      // has no valuePrefix prop (the old pass-through was silently ignored), so neither
      // call forwards it.
      if (hasCuts) {
        return (
          <Sized grow>
            <SegmentBandBlocks
              anchorResponses={resp}
              cutSlices={cutSlices}
              mode={segMode(q.choices?.length ?? 2)}
              renderBand={(band) => (
                <NumericChart question={q} responses={band} colors={colors} />
              )}
            />
          </Sized>
        )
      }
      return (
        <Sized grow>
          <NumericChart question={q} responses={resp} colors={colors} />
        </Sized>
      )
    case 'quadrant': {
      // A 2-axis matrixdropdown is a POSITIONING study (e.g. importance × satisfaction,
      // appeal × clarity): each option is a point placed by its mean on each axis, so the
      // marketer reads "who sits top-right" at a glance. Render the real scatter QuadrantChart
      // (slide tokens are wired app-wide, so it themes cleanly here). Fall back to the score
      // grid when the shape isn't a clean 2-axis matrix.
      const renderQuadrant = (rows: TwinResponse[]): ReactNode => {
        const quad = buildQuadrant(q, rows)
        if (quad) {
          // Neutral per-option colours from the app palette — NOT QuadrantChart's default
          // 'semantic' mode, whose red/green zones encode ad-creative semantics (trust ×
          // attention) that would mislead on a generic positioning study (e.g. a pain
          // importance × satisfaction grid). Each option just gets a distinct palette hue.
          const dotColorsByName = Object.fromEntries(
            quad.items.map((it, i) => [it.name, colors[i % colors.length]]),
          )
          // A row with no numeric answer on an axis has no position: it is left off the
          // map rather than drawn at the scale's minimum.
          const placed = quad.items.filter(
            (it): it is QuadrantPoint & { x: number; y: number } => it.x !== null && it.y !== null,
          )
          return (
            <Sized h={420}>
              <div data-testid="qs-quadrant" className="h-full">
                <QuadrantChart
                  items={placed}
                  xAxisLabel={quad.xLabel}
                  yAxisLabel={quad.yLabel}
                  xScaleMin={quad.xMin}
                  xScaleMax={quad.xMax}
                  yScaleMin={quad.yMin}
                  yScaleMax={quad.yMax}
                  dotColorsByName={dotColorsByName}
                  showInlineLabels
                />
              </div>
            </Sized>
          )
        }
        const matrixInput = prepareMatrixChartInput(q, rows)
        return (
          <Sized h={matrixChartHeight(matrixInput.question)}>
            <MatrixChart {...matrixInput} colors={colors} />
          </Sized>
        )
      }
      // Compare segments (Mode B): a scatter can't stack, so re-slice `resp` per cut and
      // toggle ONE cut's positioning at a time (Everyone anchor + n= per band, thin-cell
      // caveat) through the shared toggle host.
      if (hasCuts) {
        return <SegmentBandBlocks anchorResponses={resp} cutSlices={cutSlices} mode="toggle" renderBand={renderQuadrant} />
      }
      // Plots plan O: a new result carries Python's `quadrant` plot (in-scale means on the
      // 0-100 plane, midpoint stamped, a missing axis left out and disclosed). Each option
      // keeps its own palette slot, as the browser map gave it a hue of its own.
      if (quadrantQuickStudyAdapter.accepts(r)) {
        const { data } = quadrantQuickStudyAdapter.read(r)
        return (
          <Sized h={420}>
            <div data-testid="qs-quadrant" className="h-full">
              <QuadrantPlot
                data={data}
                props={{ colorMode: 'byItem' }}
                shownOfTotalTemplate={quadrantShownOfTotal}
                testId="qs-quadrant-plot"
              />
            </div>
          </Sized>
        )
      }
      // W6-QS-b: a grid that is not a map draws Python's heat grid, not the browser score grid.
      if (isGridReadable(r)) {
        return <Sized grow>{stampedGrid}</Sized>
      }
      return renderQuadrant(resp)
    }
    case 'matrix':
    case 'heatmap': {
      // A 3+ attribute / single-metric grid renders as a per-option scorecard grid (each
      // option's metrics, with a colour legend). The headline names the dual-axis leader.
      // Grow (natural height) rather than a fixed height that can under-size a many-row
      // heatmap table and clip its bottom rows — the report box scrolls when it's tall.
      const renderMatrix = (rows: TwinResponse[]): ReactNode => {
        const matrixInput = prepareMatrixChartInput(q, rows)
        return (
          <Sized grow>
            <MatrixChart {...matrixInput} colors={colors} />
          </Sized>
        )
      }
      // Compare segments (Mode B): the heatmap grid can't stack, so toggle ONE cut's grid at
      // a time, each re-derived from that cut's raw responses (client-side, zero backend).
      if (hasCuts) {
        return <SegmentBandBlocks anchorResponses={resp} cutSlices={cutSlices} mode="toggle" renderBand={renderMatrix} />
      }
      // W6-QS-b: the grid Python stamped (a rating grid's heat grid, a categorical grid's bars).
      if (isGridReadable(r)) {
        return <Sized grow>{stampedGrid}</Sized>
      }
      return renderMatrix(resp)
    }
    // Synthetic OE theme-view ids (the Heatmap/Bars switch rides plotOverride). They map to
    // `themesView` above and must route to the SAME open-end renderer, not fall through to the
    // generic `default:` quant path when a malformed card lacks a comment/text question.
    case 'text_responses':
    case 'open_end':
    case 'themes':
    case 'themes_heatmap':
    case 'themes_bars':
    case 'word_cloud':
    case 'wordcloud':
      return renderOpenEndInsights(r, q, resp, personas, comparisonLabels, populationId, onJumpToSource, creativeTitles, inReport, openEndTab, onOpenEndTabChange, openEndQuoteResponses, openEndQuoteFilteredEmpty, themesView)

    case 'nps_diverging': {
      // The canonical NPS diverging bar (detractors vs promoters). Only valid for a
      // 0–10 NPS rating; if the data isn't NPS-shaped, fall back to the grouped-bar
      // bucket view so the switch never renders an empty/incorrect chart.
      const renderNps = (rows: TwinResponse[]): ReactNode => {
        const nps = buildNpsDiverging(q, rows)
        if (nps) {
          // Chat (intentView): the PlotCard footnote carries "Base: N twins", so the
          // exhibit's internal n= line is retired there — one base-size line per card.
          return <Sized grow><NpsDivergingBar data={nps} showBaseSize={!intentView} /></Sized>
        }
        const buckets = buildNpsBuckets(q, rows)
        // Plots plan J: the whole-run split Python stamped (never a cut band's rows).
        if (buckets && rows === resp && !r.monadic && q?.type === 'rating' && hasQuickStudyChoicePlot(r)) {
          return <Sized grow><QuickStudyChoicePlot result={r} /></Sized>
        }
        if (buckets) {
          return <Sized grow><SingleChoiceChart question={buckets.question} responses={buckets.responses} type="bar" colors={colors} includeEmptyChoices /></Sized>
        }
        return <Sized h={300}><NumericChart question={q} responses={rows} colors={colors} valuePrefix={r.currency} /></Sized>
      }
      // Compare segments (Mode B): NPS is one centered bar per group, so toggle ONE cut's
      // detractor/passive/promoter split at a time (each recomputed off that cut's raw 0–10
      // answers) rather than stacking, keeping the card compact.
      if (hasCuts) {
        return <SegmentBandBlocks anchorResponses={resp} cutSlices={cutSlices} mode="toggle" renderBand={renderNps} />
      }
      // W6-QS: the stamped NPS read (Average | NPS views, the score from the rating statistics,
      // the count legend). W7 Q7: a plot Python read as a plain rating draws that likert plot,
      // never the browser NPS bar.
      if (likertPlot) {
        return likertPlot
      }
      return renderNps(resp)
    }

    case 'radar': {
      // Monadic scorecard read as a radar: build from the aggregate (Everyone-level, identical
      // numbers to the heatmap). No per-cut toggle here — the scorecard's Compare-segments picker
      // lives in the heatmap exhibit (unmounted in radar view) and scorecard cards aren't in the
      // generic segmentable set, so there are no cuts to honor. Non-scorecard radars (e.g. emotion
      // profiles) fall through to the response-fed `buildRadar` path below, untouched.
      // W6-QS: the grid Python stamped, drawn as its radar alternative (the heat grid when a
      // radar would misstate it). Scorecards and emotion profiles alike: each item's twins
      // answer only their own row, so the pooled grid is the per-item read.
      if (!hasCuts && isGridReadable(r)) {
        return (
          <Sized grow>
            <QuickStudyGridPlot result={r} view="radar" />
          </Sized>
        )
      }
      // SC: a scorecard stored without its plot draws its radar from the stored aggregate,
      // shape only (legacy adapter), through the study radar.
      if (r.monadic && quickStudyScorecardRadarAdapter.accepts(r)) {
        return (
          <Sized grow>
            <div data-testid="qs-scorecard-legacy-radar">
              <RadarPlot data={quickStudyScorecardRadarAdapter.read(r).data} testIdPrefix="qs-scorecard-legacy-radar-plot" />
            </div>
          </Sized>
        )
      }
      const renderRadar = (rows: TwinResponse[]): ReactNode => {
        const radar = buildRadar(q, rows)
        if (!radar) {
          const matrixInput = prepareMatrixChartInput(q, rows)
          return (
            <Sized h={matrixChartHeight(matrixInput.question)}>
              <MatrixChart {...matrixInput} colors={colors} />
            </Sized>
          )
        }
        // Reserve extra vertical room for the per-series colour legend as the series
        // count grows, so a dense 3-series radar's legend isn't clipped.
        const radarHeight = Math.max(360, 320 + radar.series.length * 18)
        return (
          <Sized h={radarHeight}>
            <RadarChart labels={radar.labels} series={radar.series} min={radar.min} max={radar.max} />
          </Sized>
        )
      }
      // Compare segments (Mode B): a radial can't stack, so toggle ONE cut's radar at a time,
      // each re-derived from that cut's raw responses (the polygon means recompute per cut).
      if (hasCuts) {
        return <SegmentBandBlocks anchorResponses={resp} cutSlices={cutSlices} mode="toggle" renderBand={renderRadar} />
      }
      return renderRadar(resp)
    }
    case 'grouped_bar': {
      const renderGroupedBar = (rows: TwinResponse[]): ReactNode => {
        if (q.type === 'ranking') {
          return <Sized grow><RankingChart question={q} responses={rows} colors={colors} labels={rankingLabels} /></Sized>
        }
        // NPS (0-10 rating): render the promoter/passive/detractor split — the actual
        // deliverable — instead of a flat 0-10 distribution.
        if (q.type === 'rating') {
          const nps = buildNpsBuckets(q, rows)
          if (nps) {
            return <Sized grow><SingleChoiceChart question={nps.question} responses={nps.responses} type="bar" colors={colors} includeEmptyChoices /></Sized>
          }
          return <Sized h={300}><NumericChart question={q} responses={rows} colors={colors} valuePrefix={r.currency} /></Sized>
        }
        if (q.type === 'text') {
          return <Sized h={480}><NumericChart question={q} responses={rows} colors={colors} valuePrefix={r.currency} /></Sized>
        }
        return <Sized grow><SingleChoiceChart question={q} responses={rows} type="bar" colors={colors} includeEmptyChoices /></Sized>
      }
      // Compare segments (Mode B): toggle ONE cut's grouped bars at a time, each recomputed
      // off that cut's raw responses (client-side, zero backend).
      if (hasCuts) {
        return <SegmentBandBlocks anchorResponses={resp} cutSlices={cutSlices} mode="toggle" renderBand={renderGroupedBar} />
      }
      // W6-QS: the whole-run reads Python stamped. NPS: J's detractor / passive / promoter split;
      // another rating: the diverging-likert read; a numeric answer: the histogram; a choice: the
      // choice plot. Ranking stays on its own branch (task I).
      // W7 Q6: a ranking draws its stamped ranking read (never the browser RankingChart).
      if (q.type === 'ranking' && quickStudyRankingAdapter.accepts(r)) {
        return renderRankingTotal()
      }
      if (q.type === 'rating' && !r.monadic && hasQuickStudyChoicePlot(r)) {
        return <Sized grow><QuickStudyChoicePlot result={r} /></Sized>
      }
      if (q.type === 'rating' && likertPlot) {
        return likertPlot
      }
      if (q.type === 'text' && densityHistogram) {
        return <Sized grow>{densityHistogram}</Sized>
      }
      if (!r.monadic && isChoiceQuestion(q) && hasQuickStudyChoicePlot(r)) {
        return <Sized grow><QuickStudyChoicePlot result={r} /></Sized>
      }
      return renderGroupedBar(resp)
    }
    case 'psm': {
      // Read before the adapter narrows `r`: the legacy aggregate is drawn when no plot is stamped.
      const legacyPsm = r.psm ?? {}
      // Van Westendorp price sensitivity (plots plan G). A new result carries the canonical
      // curve as a `price-curves` plot (Python `build_price_curve` through the quick-study
      // adapter); it draws like the report card, with the chrome in the viewer's language.
      if (quickStudyPriceCurvesAdapter.accepts(r)) {
        const { data } = quickStudyPriceCurvesAdapter.read(r)
        return (
          <Sized grow>
            <PriceCurvesPlot data={{ ...data, labels: priceCurvesLabels }} props={{ size: 'card', fit: 'card' }} testIdPrefix="qs-price-curves" />
          </Sized>
        )
      }
      // A refused curve states the producer's typed reason (the code the headline reads)
      // in the viewer's language, falling back to the stamped run-language copy, with the
      // plot's own notice. Nothing is drawn: the canonical producer publishes no curves.
      if (priceCurveRefusalText) {
        return (
          <Sized grow>
            <PriceCurvesRefusal message={priceCurveRefusalText} testIdPrefix="qs-price-curves" />
          </Sized>
        )
      }
      // LEGACY reader: a stored result carries only r.psm (the old quick-study aggregate),
      // which PSMChart draws; PsmData stays until the legacy deletion step. Chat
      // (`intentView`): the PlotCard footnote carries "Base: N twins", so the chart's own
      // n= line is suppressed there.
      return <Sized grow><PSMChart data={legacyPsm} showBaseSize={!intentView} /></Sized>
    }
    default:
      if (isOpenEndQuestion(q)) {
        return renderOpenEndInsights(r, q, resp, personas, comparisonLabels, populationId, onJumpToSource, creativeTitles, inReport, openEndTab, onOpenEndTabChange, openEndQuoteResponses, openEndQuoteFilteredEmpty, themesView)
      }
      // Segment split for an unrecognized plot id now that the picker is offered on every
      // response-derived chart (the whitelist lift): mirror the `bar` case's band-ordered
      // small multiples so a selected cut actually re-slices instead of a dead control. The
      // pooled fall-through below is unchanged.
      if (hasCuts) {
        return (
          <Sized grow>
            <SegmentBandBlocks
              anchorResponses={resp}
              cutSlices={cutSlices}
              mode={segMode(q.choices?.length ?? 2)}
              renderBand={(band) => (
                <SingleChoiceChart question={q} responses={band} type="bar" colors={colors} includeEmptyChoices />
              )}
            />
          </Sized>
        )
      }
      return (
        <Sized grow>
          <SingleChoiceChart question={q} responses={resp} type="bar" colors={colors} includeEmptyChoices />
        </Sized>
      )
  }
}

// Compact mode for the narrative "Report" view: the SAME chart, rendered small + inline
// inside flowing prose (vs. the full-size card in the "Insights" view). Default false → every
// existing PlotRenderer usage is byte-identical. A genuine shrink, not a clip: width is capped
// (width-responsive grow charts get proportionally shorter, never cut off) and fixed-height
// canvas charts scale down via a reduced definite height.
export const PlotCompactContext = createContext(false)

// True whenever a plot renders inside the report panel (BOTH the "Findings" and narrative
// "Report" views), false in the main chat thread. Set by the report's `InlinePlot`. Distinct
// from PlotCompactContext (which is only the narrative view) so report-only affordances — e.g.
// paging a many-topic monadic theme card through a carousel — apply across the whole panel
// without changing the chat card.
export const ReportPlotContext = createContext(false)

// Fraction of a canvas chart's natural height kept in compact mode.
const COMPACT_HEIGHT_SCALE = 0.66

// `grow` charts (bar/ranking/rating) are content-sized div/SVG with no canvas, so
// they render at exactly their natural height — taller as segments are added. We
// reserve NO height for them: the card footer flows directly below, never
// overlapping (the old overflow bug) and never leaving an empty gap. Canvas charts
// (pie doughnut / radar) DO need a definite height, so they pass `h` and omit `grow`.
function Sized({ h, grow, children }: { h?: number; grow?: boolean; children: React.ReactNode }) {
  const compact = useContext(PlotCompactContext)
  // `chart-app-surface` remaps the deck-ui slide TEXT/label tokens to the theme-aware chat
  // tokens in dark mode (see globals.css), so every DOM graph's labels stay readable on the
  // app surface without touching slide rendering. Canvas graphs get the same treatment via
  // the global `chartJsThemeTextPlugin`.
  // Compact: cap the width (the chart reflows narrower, so it's shorter too — no clipping)
  // and, for definite-height canvas charts, scale the height down rather than clip it.
  if (grow) return <div className={cn('chart-app-surface', compact && 'max-w-md')}>{children}</div>
  const height = compact && h ? Math.round(h * COMPACT_HEIGHT_SCALE) : h
  return <div className={cn('chart-app-surface', compact && 'max-w-md')} style={{ height }}>{children}</div>
}

// NPS (0-10 rating) → the promoter/passive/detractor split is the deliverable, not a
// flat 0-10 histogram. Re-shape the numeric answers into a 3-bucket radiogroup so the
// existing bar chart draws Detractors/Passives/Promoters with a clean legend; matches
// the NPS headline from analysis.py. Returns null for non-NPS ratings.
export function buildNpsBuckets(
  q: SurveyQuestion,
  resp: TwinResponse[],
): { question: SurveyQuestion; responses: TwinResponse[] } | null {
  if (q.rateMin !== 0 || q.rateMax !== 10) return null
  const name = q.name
  const order = ['Detractors (0–6)', 'Passives (7–8)', 'Promoters (9–10)']
  const responses: TwinResponse[] = []
  for (const rr of resp) {
    const v = rr.survey_data?.[name]
    if (typeof v !== 'number') continue
    // The one NPS rule (plots plan P): policy cuts on the raw answer, never pre-rounded.
    const group = npsGroupOf(v)
    const bucket = group === 'promoters' ? order[2] : group === 'passives' ? order[1] : order[0]
    responses.push({ survey_data: { [name]: bucket }, attributes: rr.attributes })
  }
  if (!responses.length) return null
  const question: SurveyQuestion = { ...q, type: 'radiogroup', choices: order.map((t) => ({ value: t, text: t })) }
  return { question, responses }
}

// NPS (0-10 rating) → the honest promoter/passive/detractor split + the NPS score, for
// the DIVERGING-bar viz (detractors on one side, promoters on the other — the canonical,
// clearer NPS picture than a grouped bar). PURE: tallies counts on the raw 0-10 answers
// (promoter>=9 / passive 7-8 / detractor<=6), derives each share off the SAME usable
// base, and computes nps = promoters% − detractors% (matching analysis.py's headline).
// Returns null for a non-NPS rating or when no usable numeric answers landed.
export function buildNpsDiverging(
  q: SurveyQuestion,
  resp: TwinResponse[],
): { detractors: number; passives: number; promoters: number; n: number; detractorPct: number; passivePct: number; promoterPct: number; nps: number } | null {
  if (q.rateMin !== 0 || q.rateMax !== 10) return null
  const name = q.name
  // The one NPS rule (plots plan P, `npsSplit`): policy cuts on raw answers, unrounded
  // shares, the score from the unrounded shares, each figure rounded once half-even:
  // the same figures as analysis.py's headline.
  return npsSplit(resp.map((rr) => rr.survey_data?.[name]))
}

// Minimal, honest diverging NPS bar built from primitives (no new dependency): a single
// 100%-wide track centered at the midline — detractors grow LEFT, promoters grow RIGHT,
// passives sit neutral in the middle — with the literal counts + percents and the NPS
// score beside it.
//
// Colours are SEMANTIC, not palette-ordered: detractors = danger/red, passives =
// neutral/gray, promoters = positive/green, using the app's chart-* semantic tokens
// (--chart-negative / --chart-neutral / --chart-positive in globals.css). This keeps the
// three buckets clearly distinct — the old palette-index colours read passive/promoter
// as two near-identical greens — and theme-safe in both light and dark.
const NPS_DETRACTOR_COLOR = 'hsl(var(--chart-negative))' // detractors — danger / red
const NPS_PASSIVE_COLOR = 'hsl(var(--chart-neutral))' // passives — neutral / gray
const NPS_PROMOTER_COLOR = 'hsl(var(--chart-positive))' // promoters — positive / green
function NpsDivergingBar({
  data,
  showBaseSize = true,
}: {
  data: NonNullable<ReturnType<typeof buildNpsDiverging>>
  /** The chat PlotCard's footnote carries base-size honesty ("Base: N twins"), so the
   *  exhibit's own n= line renders only where no card footnote exists (report/preview). */
  showBaseSize?: boolean
}) {
  const { detractors, passives, promoters, n, detractorPct, passivePct, promoterPct, nps } = data
  // Honest widths: each segment is its share of the SAME n, so the three fill the track.
  const detW = (100 * detractors) / n
  const pasW = (100 * passives) / n
  const proW = (100 * promoters) / n
  const detColor = NPS_DETRACTOR_COLOR
  const pasColor = NPS_PASSIVE_COLOR
  const proColor = NPS_PROMOTER_COLOR
  // Always signed, as on every NPS surface: "+25", "−5", "0".
  const score = formatNpsScore(nps)
  return (
    <div className="space-y-3" data-testid="qs-nps-diverging">
      <div className="flex items-baseline justify-between">
        <span className="text-xs text-twin-chat-fg-muted">NPS</span>
        <span className="text-lg font-semibold text-twin-chat-fg" data-testid="qs-nps-score">
          {score}
        </span>
      </div>
      {/* One diverging track: detractors then passives then promoters, left→right, rendered
          as per-segment rounded elements with gaps via the shared DivergingBar (its own
          zero line is hidden — the NPS boundary is the detractor edge, not a fixed 50%).
          A subtle divider sits where the detractor (red) share ends, marking the
          negative-vs-rest boundary, only when there's something on both sides. */}
      <div className="relative">
        <DivergingBar
          segments={[
            { leftPct: 0, widthPct: detW, color: detColor, value: `${detractors} (${detractorPct}%)`, label: 'Detractors (0–6)' },
            { leftPct: detW, widthPct: pasW, color: pasColor, value: `${passives} (${passivePct}%)`, label: 'Passives (7–8)' },
            { leftPct: detW + pasW, widthPct: proW, color: proColor, value: `${promoters} (${promoterPct}%)`, label: 'Promoters (9–10)' },
          ]}
          ariaLabel={`NPS ${score}: ${promoterPct}% promoters, ${passivePct}% passives, ${detractorPct}% detractors`}
          zeroLineClassName="hidden"
        />
        {detW > 0 && detW < 100 && (
          <span
            aria-hidden
            className="pointer-events-none absolute inset-y-0 w-px bg-twin-chat-surface/70"
            style={{ left: `${detW}%` }}
            data-testid="qs-nps-divider"
          />
        )}
      </div>
      {/* detColor/pasColor/proColor are the semantic NPS tokens above. */}
      <PlotDotLegend
        className="text-xs text-twin-chat-fg-muted"
        items={[
          {
            color: detColor,
            label: (
              <>
                Detractors (0–6): <span className="font-medium text-twin-chat-fg">{detractors}</span> ({detractorPct}%)
              </>
            ),
          },
          {
            color: pasColor,
            label: (
              <>
                Passives (7–8): <span className="font-medium text-twin-chat-fg">{passives}</span> ({passivePct}%)
              </>
            ),
          },
          {
            color: proColor,
            label: (
              <>
                Promoters (9–10): <span className="font-medium text-twin-chat-fg">{promoters}</span> ({promoterPct}%)
              </>
            ),
          },
        ]}
      />
      {showBaseSize && <p className="text-[11px] text-twin-chat-fg-muted">n={n}</p>}
    </div>
  )
}

function _mean(xs: number[]): number {
  return xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : NaN
}

// matrixdropdown answer: survey_data[qname] = { rowId: { colId: rating } }

/** True when the grid has fewer entities than attributes (e.g. 2 creatives × 8 emotions). */
export function shouldTransposeMatrixDropdown(q: SurveyQuestion): boolean {
  if (q.type !== 'matrixdropdown' && q.type !== 'matrixdynamic') {
    return false
  }
  const rowCount = q.rows?.length ?? 0
  const colCount = q.columns?.length ?? 0
  return rowCount >= 1 && colCount > rowCount
}

/** Flip rows ↔ columns so entities (creatives) read across the top and attributes down the side. */
export function transposeMatrixDropdown(
  q: SurveyQuestion,
  responses: TwinResponse[],
): { question: SurveyQuestion; responses: TwinResponse[] } {
  const oldRows = q.rows ?? []
  const oldCols = q.columns ?? []
  if (!oldRows.length || !oldCols.length) {
    return { question: q, responses }
  }

  const templateCol = oldCols[0] as any
  const sharedChoices = templateCol?.choices ?? []
  const rateMin = templateCol?.rateMin ?? (q as any).rateMin
  const rateMax = templateCol?.rateMax ?? (q as any).rateMax

  const newRows = oldCols.map((col: any) => ({
    value: String(col?.name ?? col?.value ?? col),
    text: col?.title || col?.text || col?.name || col,
  }))

  const newColumns = oldRows.map((row: any) => {
    const rowKey = String(row?.value ?? row?.id ?? row)
    return {
      name: rowKey,
      title: row?.text || row?.title || rowKey,
      choices: sharedChoices,
      ...(rateMin != null ? { rateMin } : {}),
      ...(rateMax != null ? { rateMax } : {}),
    }
  })

  const question: SurveyQuestion = {
    ...q,
    rows: newRows,
    columns: newColumns,
  }

  const transposedResponses = responses.map((resp) => {
    const matrixData = resp.survey_data?.[q.name]
    if (!matrixData || typeof matrixData !== 'object') {
      return resp
    }

    const flipped: Record<string, Record<string, unknown>> = {}
    for (const oldRow of oldRows) {
      const rowKey = String((oldRow as any)?.value ?? (oldRow as any)?.id ?? oldRow)
      const rowData = (matrixData as Record<string, unknown>)[rowKey]
      if (!rowData || typeof rowData !== 'object') {
        continue
      }
      for (const oldCol of oldCols) {
        const colKey = String((oldCol as any)?.name ?? (oldCol as any)?.value ?? oldCol)
        const val = (rowData as Record<string, unknown>)[colKey]
        if (val === undefined) {
          continue
        }
        flipped[colKey] ||= {}
        flipped[colKey][rowKey] = val
      }
    }

    return {
      ...resp,
      survey_data: {
        ...resp.survey_data,
        [q.name]: flipped,
      },
    }
  })

  return { question, responses: transposedResponses }
}

function prepareMatrixChartInput(
  q: SurveyQuestion,
  responses: TwinResponse[],
): { question: SurveyQuestion; responses: TwinResponse[] } {
  if (!shouldTransposeMatrixDropdown(q)) {
    return { question: q, responses }
  }
  return transposeMatrixDropdown(q, responses)
}

function matrixChartHeight(q: SurveyQuestion): number {
  const rowCount = q.rows?.length ?? 3
  return Math.max(260, 64 * rowCount + 96)
}

// Categorical series palette for multi-series radars (Page A vs Page B vs …). The deck
// `--slide-chart-*` ramp is monochromatic green (all hue ~160), so relying on it makes two
// series read as the same colour; this ordered set keeps maximal HUE separation between the
// first entries — brand teal for series 0, orange for series 1 — so each polygon is instantly
// distinguishable. Frontend semantic tokens (resolved on-canvas via resolveCanvasColor).
const RADAR_SERIES_COLORS = [
  'hsl(var(--chart-primary))', // teal (brand) — series 0
  'hsl(var(--chart-accent))', // orange — series 1
  'hsl(var(--chart-tertiary))', // blue
  'hsl(var(--chart-quinary))', // purple
  'hsl(var(--chart-senary))', // cyan
  'hsl(var(--chart-secondary))', // green
] as const

export function buildRadar(
  q: SurveyQuestion,
  responses: TwinResponse[]
): { labels: string[]; series: RadarSeries[]; min: number; max: number } | null {
  const rows = q.rows ?? []
  const cols = q.columns ?? []
  if (cols.length < 3 || rows.length < 1) return null
  const labels = cols.map((c: any) => c.title || c.name)
  // Dense radars (e.g. Claims 3×8) overlap badly when every polygon is filled — the
  // translucent areas stack into one muddy teal blob with no readable per-series shape.
  // Fill ONLY a single-series radar; with 2+ series draw OUTLINE-ONLY polygons so each
  // series stays distinct against the colour legend RadarChart already renders.
  const fillAreas = rows.length <= 1
  const series: RadarSeries[] = rows.map((row: any, idx: number) => {
    const rid = row.value ?? row.id
    const data = cols.map((col: any) => {
      const xs: number[] = []
      for (const resp of responses) {
        const cell = resp.survey_data?.[q.name]?.[rid]
        if (cell && typeof cell[col.name] === 'number') xs.push(cell[col.name])
      }
      const m = _mean(xs)
      return Number.isFinite(m) ? Number(m.toFixed(2)) : 0
    })
    return { label: row.text || rid, data, fill: fillAreas, color: RADAR_SERIES_COLORS[idx % RADAR_SERIES_COLORS.length] }
  })
  const min = cols[0]?.rateMin ?? 1
  const max = cols[0]?.rateMax ?? 5
  return { labels, series, min, max }
}

// A ranking answer is an ORDERING ARRAY (survey_data[name] = [choiceVal, …]). The
// DISTRIBUTION read of a ranking question is the share-of-#1-vote per option: the % of
// twins who ranked each option FIRST. This is a genuinely different series from the
// averaged-order RankingChart, so the Ranking↔Distribution toggle stops being a no-op.
// Survey-agnostic: keyed only on the question's own choices, never on option text.
// Returns null for a non-ranking question or when no usable ordering answers landed.
export function buildRankingFirstChoice(
  q: SurveyQuestion,
  resp: TwinResponse[],
): MatrixBarDatum[] | null {
  if (q.type !== 'ranking') return null
  const choices: Array<any> = (q as any).choices ?? []
  if (!choices.length) return null
  const firsts = new Map<string, number>()
  let total = 0
  for (const rr of resp) {
    const v = rr.survey_data?.[q.name]
    if (!Array.isArray(v) || !v.length) continue
    total++
    const head = String(v[0])
    firsts.set(head, (firsts.get(head) ?? 0) + 1)
  }
  if (!total) return null
  return choices
    .map((c: any) => {
      const val = String(c?.value ?? c)
      const count = firsts.get(val) ?? 0
      return { label: c?.text ?? c?.title ?? val, value: Math.round((100 * count) / total) }
    })
    .sort((a, b) => b.value - a.value)
}

export interface QuadrantPoint {
  name: string
  /** null = no numeric answer on this axis (never plotted at 0). */
  x: number | null
  y: number | null
}

// A 2-AXIS matrixdropdown (exactly two rating columns) → a positioning scatter: one point
// per row (option), x = the row's mean on column 0, y = its mean on column 1. Axis labels
// come from the column titles, and each axis keeps its OWN scale (the two columns may differ,
// e.g. a 0–100 price beside a 1–5 appeal). Mirrors buildRadar's matrixdropdown cell read
// (survey_data[qname][rowId][colName]); only numeric cells count. Returns null for anything
// that isn't a clean 2-column grid so the caller falls back to the MatrixChart score grid.
export function buildQuadrant(
  q: SurveyQuestion,
  responses: TwinResponse[],
): { items: QuadrantPoint[]; xLabel: string; yLabel: string; xMin: number; xMax: number; yMin: number; yMax: number } | null {
  const rows = q.rows ?? []
  const cols = q.columns ?? []
  if (cols.length !== 2 || rows.length < 1) return null
  const cx: any = cols[0]
  const cy: any = cols[1]
  const items: QuadrantPoint[] = rows.map((row: any) => {
    const rid = row.value ?? row.id
    const xs: number[] = []
    const ys: number[] = []
    for (const resp of responses) {
      const cell = resp.survey_data?.[q.name]?.[rid]
      if (cell && typeof cell[cx.name] === 'number') xs.push(cell[cx.name])
      if (cell && typeof cell[cy.name] === 'number') ys.push(cell[cy.name])
    }
    const mx = _mean(xs)
    const my = _mean(ys)
    return {
      name: row.text || String(rid),
      x: Number.isFinite(mx) ? Number(mx.toFixed(2)) : null,
      y: Number.isFinite(my) ? Number(my.toFixed(2)) : null,
    }
  })
  return {
    items,
    xLabel: cx.title || cx.name,
    yLabel: cy.title || cy.name,
    xMin: typeof cx.rateMin === 'number' ? cx.rateMin : 1,
    xMax: typeof cx.rateMax === 'number' ? cx.rateMax : 5,
    yMin: typeof cy.rateMin === 'number' ? cy.rateMin : 1,
    yMax: typeof cy.rateMax === 'number' ? cy.rateMax : 5,
  }
}

export interface MatrixBarDatum {
  label: string
  value: number
}

// The two render-only reads a single-metric matrixdropdown bar can show per row:
//   'mean'       — the average rating (the score axis read).
//   'top_2_box'  — the % of in-scale cells in the TOP TWO BOXES (rating >= colMax − 1),
//                  the intent / likelihood-to-act read marketers use, as a 0–100 number.
export type MatrixBarMetric = 'mean' | 'top_2_box'

// A SINGLE-metric matrixdropdown (one rating column scored across N rows/creatives —
// e.g. "Likelihood to act" for Creative A vs Creative B) is a bar-per-row shape, not a
// 2-cell heatmap. Its answer is survey_data[qname] = { rowId: { colId: rating } }, so we
// return one entry per row keyed off the requested `metric`:
//   mean       → { label, value: meanRatingForThatRow } (2dp).
//   top_2_box  → { label, value: % of that row's usable cells with rating >= colMax−1 }.
// Only numeric cells count (missing / non-numeric cells are ignored; a row with no usable
// cell yields 0 — an honest empty bar). Returns null for a multi-column matrixdropdown so
// the existing matrix/heatmap path keeps owning the 2-axis scorecard.
export function buildMatrixBar(
  q: SurveyQuestion,
  responses: TwinResponse[],
  metric: MatrixBarMetric = 'mean',
): MatrixBarDatum[] | null {
  if (q.type !== 'matrixdropdown' && q.type !== 'matrixdynamic') return null
  const rows = q.rows ?? []
  const cols = q.columns ?? []
  if (cols.length !== 1 || rows.length < 1) return null
  const col0 = cols[0] ?? {}
  const colName = col0.name ?? col0.value
  // Top-2-box threshold rides off the column's own scale max (default 5): the top two
  // boxes are rating >= colMax − 1 (e.g. 4 and 5 on a 1–5 likelihood scale).
  const colMax = typeof col0.rateMax === 'number' ? col0.rateMax : 5
  return rows.map((row: any) => {
    const rid = row.value ?? row.id
    const xs: number[] = []
    for (const resp of responses) {
      const cell = resp.survey_data?.[q.name]?.[rid]
      const v = cell?.[colName]
      if (typeof v === 'number' && Number.isFinite(v)) xs.push(v)
    }
    if (metric === 'top_2_box') {
      if (!xs.length) return { label: row.text || String(rid), value: 0 }
      const top = xs.filter((v) => v >= colMax - 1).length
      return { label: row.text || String(rid), value: Math.round((100 * top) / xs.length) }
    }
    const m = _mean(xs)
    return { label: row.text || String(rid), value: Number.isFinite(m) ? Number(m.toFixed(2)) : 0 }
  })
}

export interface MatrixDivergingDatum {
  label: string
  bottom2: number
  neutral: number
  top2: number
  n: number
  bottom2Pct: number
  neutralPct: number
  top2Pct: number
}

// The top-2-box view of a single-metric matrixdropdown as a DIVERGING STACKED bar per
// row — the gold-standard Likert read leading research tools (Qualtrics et al.) use:
// bottom-2 boxes (red, the rejecters) ← neutral (grey) → top-2 boxes (green), so the
// downside is visible, not just the headline top-2. Each in-scale cell is bucketed ONCE:
// top-2 if rating >= rateMax−1, else bottom-2 if rating <= rateMin+1, else neutral.
// Returns null for a multi-column matrixdropdown / non-matrix (those keep their own path).
export function buildMatrixDiverging(
  q: SurveyQuestion,
  responses: TwinResponse[],
): MatrixDivergingDatum[] | null {
  if (q.type !== 'matrixdropdown' && q.type !== 'matrixdynamic') return null
  const rows = q.rows ?? []
  const cols = q.columns ?? []
  if (cols.length !== 1 || rows.length < 1) return null
  const col0 = cols[0] ?? {}
  const colName = col0.name ?? col0.value
  const rateMin = typeof col0.rateMin === 'number' ? col0.rateMin : 1
  const rateMax = typeof col0.rateMax === 'number' ? col0.rateMax : 5
  return rows.map((row: any) => {
    const rid = row.value ?? row.id
    let bottom2 = 0
    let neutral = 0
    let top2 = 0
    for (const resp of responses) {
      const cell = resp.survey_data?.[q.name]?.[rid]
      const v = cell?.[colName]
      if (typeof v !== 'number' || !Number.isFinite(v)) {
        continue
      }
      if (v >= rateMax - 1) {
        top2++
      } else if (v <= rateMin + 1) {
        bottom2++
      } else {
        neutral++
      }
    }
    const n = bottom2 + neutral + top2
    const pct = (x: number): number => (n ? Math.round((100 * x) / n) : 0)
    return {
      label: row.text || String(rid),
      bottom2,
      neutral,
      top2,
      n,
      bottom2Pct: pct(bottom2),
      neutralPct: pct(neutral),
      top2Pct: pct(top2),
    }
  })
}

// Semantic box colours (shared with the NPS diverging bar): bottom-2 = danger/red,
// neutral = grey, top-2 = positive/green — theme-safe via the chart-* tokens.
const BOX_BOTTOM_COLOR = 'hsl(var(--chart-negative))'
const BOX_NEUTRAL_COLOR = 'hsl(var(--chart-neutral))'
const BOX_TOP_COLOR = 'hsl(var(--chart-positive))'

// A TRUE diverging stacked bar: every row shares a fixed zero line at the track's
// centre (50%). Bottom-2 grows LEFT of it, top-2 grows RIGHT, and neutral straddles it
// (the centre line bisects the neutral block) — so the rows are comparable at a glance
// ("how far right = how favourable") instead of every bar left-anchored independently.
// Widths are in CONTAINER-% where the full track (100%) spans the −100%…+100% range, so
// a respondent share s maps to s/2 container-%, and the left spacer
// (50 − bottom2/2 − neutral/4) lands the neutral midpoint exactly on the centre.
export function divergingSegments(row: MatrixDivergingDatum): {
  spacerLeftPct: number
  bottomPct: number
  neutralPct: number
  topPct: number
} {
  const n = row.n
  const fb = n ? row.bottom2 / n : 0
  const fnu = n ? row.neutral / n : 0
  const ft = n ? row.top2 / n : 0
  return {
    spacerLeftPct: (0.5 - fb / 2 - fnu / 4) * 100,
    bottomPct: (fb / 2) * 100,
    neutralPct: (fnu / 2) * 100,
    topPct: (ft / 2) * 100,
  }
}

function MatrixDivergingBars({ rows }: { rows: Array<MatrixDivergingDatum> }) {
  return (
    <div className="space-y-3" data-testid="qs-matrix-diverging">
      <div className="space-y-2">
        {rows.map((row, rowIndex) => {
          const seg = divergingSegments(row)
          return (
            <div key={row.label} className="flex items-center gap-2">
              {/* Proportional label column (min 6rem) with a 2-line clamp — the old fixed
                  w-24 single-line truncate chopped labels while the bar track had surplus
                  width (Wave-6 punch #4); long item texts now wrap to a second line.
                  Plots plan LB: the shared truncation rule, with the (i) and the whole value. */}
              <span className="flex w-1/4 min-w-24 shrink-0 items-center gap-1">
                <ChartLabel
                  text={row.label}
                  hintIndex={rowIndex}
                  hintSize={10}
                  maxLines={2}
                  className="text-xs font-medium text-twin-chat-fg"
                  style={{ minWidth: 0 }}
                />
              </span>
              {/* Per-segment rounded elements with gaps + the shared PlotTooltip via the
                  shared DivergingBar primitive (same conversion as NpsDivergingBar); the
                  gap-aware inset keeps the neutral midpoint exactly on the 50% zero line. */}
              <DivergingBar
                segments={[
                  { leftPct: seg.spacerLeftPct, widthPct: seg.bottomPct, color: BOX_BOTTOM_COLOR, value: `${row.bottom2} (${row.bottom2Pct}%)`, label: 'Bottom-2 box' },
                  { leftPct: seg.spacerLeftPct + seg.bottomPct, widthPct: seg.neutralPct, color: BOX_NEUTRAL_COLOR, value: `${row.neutral} (${row.neutralPct}%)`, label: 'Neutral' },
                  { leftPct: seg.spacerLeftPct + seg.bottomPct + seg.neutralPct, widthPct: seg.topPct, color: BOX_TOP_COLOR, value: `${row.top2} (${row.top2Pct}%)`, label: 'Top-2 box' },
                ]}
                ariaLabel={`${row.label}: ${row.top2Pct}% top-2 box, ${row.neutralPct}% neutral, ${row.bottom2Pct}% bottom-2 box`}
                zeroLineClassName="bg-twin-chat-fg/30"
              />
              <span className="w-10 shrink-0 text-right text-xs font-semibold text-twin-chat-fg" data-testid="qs-matrix-diverging-top2">
                {row.top2Pct}%
              </span>
            </div>
          )
        })}
      </div>
      <PlotDotLegend
        className="text-xs text-twin-chat-fg-muted"
        items={[
          { color: BOX_BOTTOM_COLOR, label: 'Bottom-2 box' },
          { color: BOX_NEUTRAL_COLOR, label: 'Neutral' },
          { color: BOX_TOP_COLOR, label: 'Top-2 box' },
        ]}
      />
    </div>
  )
}

// --------------------------------------------------------------------------- //
// Scalar (single-question) RATING → the purchase-intent read. The same bottom-2 /
// neutral / top-2 bucketing as the matrix diverging bar, but for ONE question, rendered
// the way leading research tools (Zappi / System1 / Qualtrics) show intent: the Top-2-box
// % as the hero number, the mean as a secondary stat, and a single sentiment bar so the
// downside (bottom-2) is visible, not just the headline. Survey-agnostic: keyed only on
// the rating scale (rateMin/rateMax), never on question text.
// --------------------------------------------------------------------------- //
type IntentBucket = 'bottom' | 'neutral' | 'top'
const BUCKET_COLOR: Record<IntentBucket, string> = {
  bottom: BOX_BOTTOM_COLOR,
  neutral: BOX_NEUTRAL_COLOR,
  top: BOX_TOP_COLOR,
}

export interface ScalarIntent {
  n: number
  mean: number
  scaleMin: number
  scaleMax: number
  minLabel: string
  maxLabel: string
  bottom2: number
  neutral: number
  top2: number
  bottom2Pct: number
  neutralPct: number
  top2Pct: number
  // The full per-value distribution (one entry per scale point), bucketed for colouring.
  counts: Array<{ value: number; n: number; pct: number; bucket: IntentBucket }>
}

export function buildScalarIntent(q: SurveyQuestion, responses: TwinResponse[]): ScalarIntent | null {
  if (q.type !== 'rating') return null
  const rateMin = typeof q.rateMin === 'number' ? q.rateMin : 1
  const rateMax = typeof q.rateMax === 'number' ? q.rateMax : 5
  if (!(rateMax > rateMin)) return null
  const vals: number[] = []
  for (const resp of responses) {
    const v = resp.survey_data?.[q.name]
    if (typeof v === 'number' && Number.isFinite(v)) vals.push(v)
  }
  const n = vals.length
  if (!n) return null
  const byValue = new Map<number, number>()
  let bottom2 = 0
  let neutral = 0
  let top2 = 0
  for (const v of vals) {
    byValue.set(v, (byValue.get(v) ?? 0) + 1)
    if (v >= rateMax - 1) top2++
    else if (v <= rateMin + 1) bottom2++
    else neutral++
  }
  const pct = (x: number): number => Math.round((100 * x) / n)
  const bucketOf = (v: number): IntentBucket => (v >= rateMax - 1 ? 'top' : v <= rateMin + 1 ? 'bottom' : 'neutral')
  const counts: ScalarIntent['counts'] = []
  for (let v = rateMin; v <= rateMax; v++) {
    const c = byValue.get(v) ?? 0
    counts.push({ value: v, n: c, pct: pct(c), bucket: bucketOf(v) })
  }
  const mean = vals.reduce((a, b) => a + b, 0) / n
  return {
    n,
    mean: Math.round(mean * 10) / 10,
    scaleMin: rateMin,
    scaleMax: rateMax,
    minLabel: String(q.minRateDescription || rateMin),
    maxLabel: String(q.maxRateDescription || rateMax),
    bottom2,
    neutral,
    top2,
    bottom2Pct: pct(bottom2),
    neutralPct: pct(neutral),
    top2Pct: pct(top2),
    counts,
  }
}

// QSB-4: a ScalarIntent built from ONE creative's precomputed distribution
// (monadic_rating[].distribution) instead of pooled responses — same bucketing as
// buildScalarIntent (top-2: v >= max-1, bottom-2: v <= min+1) so the per-creative
// charts read identically to the single-question one. Pure + exported for tests.
export function scalarIntentFromMonadicItem(
  item: NonNullable<Result['monadic_rating']>[number],
  q: SurveyQuestion,
): ScalarIntent | null {
  const rateMin = typeof item.scale_min === 'number' ? item.scale_min : 1
  const rateMax = typeof item.scale_max === 'number' ? item.scale_max : 5
  if (!(rateMax > rateMin)) return null
  const dist = item.distribution ?? {}
  let n = 0
  let bottom2 = 0
  let neutral = 0
  let top2 = 0
  let sum = 0
  const counts: ScalarIntent['counts'] = []
  const bucketOf = (v: number): IntentBucket => (v >= rateMax - 1 ? 'top' : v <= rateMin + 1 ? 'bottom' : 'neutral')
  for (let v = rateMin; v <= rateMax; v++) {
    const c = Number(dist[v] ?? 0) || 0
    n += c
    sum += v * c
    if (v >= rateMax - 1) top2 += c
    else if (v <= rateMin + 1) bottom2 += c
    else neutral += c
    counts.push({ value: v, n: c, pct: 0, bucket: bucketOf(v) })
  }
  if (!n) return null
  const pct = (x: number): number => Math.round((100 * x) / n)
  for (const row of counts) row.pct = pct(row.n)
  return {
    n,
    mean: Math.round((sum / n) * 10) / 10,
    scaleMin: rateMin,
    scaleMax: rateMax,
    minLabel: String(q?.minRateDescription || rateMin),
    maxLabel: String(q?.maxRateDescription || rateMax),
    bottom2,
    neutral,
    top2,
    bottom2Pct: pct(bottom2),
    neutralPct: pct(neutral),
    top2Pct: pct(top2),
    counts,
  }
}

function IntentLegend({ data }: { data: ScalarIntent }) {
  const item = (label: string, value: number) => (
    <>
      {label} <span className="font-medium text-twin-chat-fg">{value}%</span>
    </>
  )
  return (
    <PlotDotLegend
      className="text-xs text-twin-chat-fg-muted"
      items={[
        { color: BOX_TOP_COLOR, label: item('Top-2 box', data.top2Pct) },
        { color: BOX_NEUTRAL_COLOR, label: item('Neutral', data.neutralPct) },
        { color: BOX_BOTTOM_COLOR, label: item('Bottom-2 box', data.bottom2Pct) },
      ]}
    />
  )
}

function ScalarIntentChart({ data, view }: { data: ScalarIntent; view: 'summary' | 'distribution' }) {
  if (view === 'distribution') {
    const max = Math.max(...data.counts.map((c) => c.pct), 1)
    return (
      <div className="space-y-2.5" data-testid="qs-scalar-intent-distribution">
        {data.counts.map((c) => (
          <div key={c.value} className="flex items-center gap-3">
            <span className="w-4 shrink-0 text-right text-xs tabular-nums text-twin-chat-fg-muted">{c.value}</span>
            {/* Shared rounded token TrackBar (+ PlotTooltip, value leads) — replaces the
                legacy butt-jointed fill on a hardcoded bg-twin-chat-fg/5 rail. */}
            <div className="min-w-0 flex-1">
              <TrackBar
                widthPct={(c.pct / max) * 100}
                color={BUCKET_COLOR[c.bucket]}
                value={`${c.n} (${c.pct}%)`}
                label={`Rated ${c.value}`}
              />
            </div>
            <span className="w-16 shrink-0 text-right text-xs tabular-nums">
              <span className="font-semibold text-twin-chat-fg">{c.n}</span>{' '}
              <span className="text-twin-chat-fg-muted">{c.pct}%</span>
            </span>
          </div>
        ))}
        <IntentLegend data={data} />
      </div>
    )
  }
  // Summary: the hero Top-2-box read + a single left-to-right sentiment bar.
  const w = (x: number): number => (100 * x) / data.n
  return (
    <div className="space-y-4" data-testid="qs-scalar-intent-summary">
      <div className="flex items-baseline gap-3">
        <span className="text-4xl font-bold tabular-nums text-twin-chat-fg" data-testid="qs-intent-top2">
          {data.top2Pct}%
        </span>
        <div className="flex flex-col leading-tight">
          <span className="text-sm font-medium text-twin-chat-fg">in the top-2 box</span>
          {/* Base size intentionally NOT repeated here — the card footnote ("Base: N twins")
              is the single base-size line per the PlotCard footer grammar. */}
          <span className="text-xs text-twin-chat-fg-muted">
            mean {data.mean.toFixed(1)}/{data.scaleMax}
          </span>
        </div>
      </div>
      <div>
        {/* One left-to-right sentiment bar as per-segment rounded elements with gaps via
            the shared DivergingBar (+ PlotTooltip, value leads). The zero line is hidden —
            this stack is left-anchored, not centered (same idiom as NpsDivergingBar). */}
        <DivergingBar
          className="h-7"
          segments={[
            { leftPct: 0, widthPct: w(data.bottom2), color: BOX_BOTTOM_COLOR, value: `${data.bottom2} (${data.bottom2Pct}%)`, label: 'Bottom-2 box' },
            { leftPct: w(data.bottom2), widthPct: w(data.neutral), color: BOX_NEUTRAL_COLOR, value: `${data.neutral} (${data.neutralPct}%)`, label: 'Neutral' },
            { leftPct: w(data.bottom2) + w(data.neutral), widthPct: w(data.top2), color: BOX_TOP_COLOR, value: `${data.top2} (${data.top2Pct}%)`, label: 'Top-2 box' },
          ]}
          ariaLabel={`${data.top2Pct}% top-2 box, ${data.neutralPct}% neutral, ${data.bottom2Pct}% bottom-2 box`}
          zeroLineClassName="hidden"
        />
        <div className="mt-1 flex justify-between text-[11px] text-twin-chat-fg-muted">
          <span className="truncate pr-2" title={data.minLabel}>{data.minLabel}</span>
          <span className="truncate pl-2 text-right" title={data.maxLabel}>{data.maxLabel}</span>
        </div>
      </div>
      <IntentLegend data={data} />
    </div>
  )
}

// ── Compare-segments: shared band host (cut-ID slices, Mode A rows / Mode B toggle) ──
// The picker selects individual CUT IDs, so the band renderers below consume `cutSlices`
// (Array<{ cutId, label, n, responses }>) built off raw responses upstream — NOT one
// attribute's every value. Each renderer prepends the always-present "Everyone" anchor
// (the Mode A idiom, matching the diverging card at DivergingStackedRating.tsx:335-346).
// The toggle primitives (orderedSegBands / SegmentSwap / CutToggle / useActiveBand /
// BandCaveat) are hoisted to ./segmentedToggle so the Wave-3 scorecard reuses the SAME
// toggle without an import cycle.

/** Shared "not-yet-supported" note — a rerouted selection landed on a chart that can't be
 *  sliced yet (pricing). Behind `supportsSegments(plot)`; the live picker is hidden there. */
function SegmentsUnavailableNote() {
  return (
    <p
      className="rounded-md border border-twin-chat-divider bg-twin-chat-bubble/20 px-3 py-2 text-xs text-twin-chat-fg-muted"
      data-testid="qs-segments-unavailable"
    >
      {SEGMENTS_UNAVAILABLE_NOTE}
    </p>
  )
}

// QS — segmented view for the single-rating intent chart: ONE horizontal bar per band
// (Everyone anchor first, then each selected cut), so segments read side-by-side on the
// same axis. The bar value is the headline metric (top-2-box % by default, or mean).
// Falls back to the pooled intent chart when nothing usable slices out. The single
// combined bar stays compact, so it renders Mode A regardless of cut count. Every bar
// draws in the single accent hue (bands are labels, not categories); a thin-cell caveat
// closes the chart when any cut is under THIN_CELL_MIN twins.
function SegmentedScalarIntent({
  question,
  anchorResponses,
  cutSlices,
  metric,
  colors,
  view,
}: {
  question: SurveyQuestion
  anchorResponses: TwinResponse[]
  cutSlices: Array<CutIdSlice<TwinResponse>>
  metric: 'mean' | 'top_2_box'
  colors: string[]
  view: 'summary' | 'distribution'
}) {
  const isTop2 = metric !== 'mean'
  const allGroups = useMemo(
    () =>
      orderedSegBands(anchorResponses, cutSlices)
        .map((band) => ({ ...band, intent: buildScalarIntent(question, band.responses) }))
        .filter((g): g is typeof g & { intent: ScalarIntent } => g.intent != null),
    [question, anchorResponses, cutSlices],
  )
  // R1.1 — never bar a suppressed (n < CUT_SUPPRESS_N) cut; name it instead.
  const suppressedLabels = allGroups.filter((g) => g.cutId !== '__all__' && g.suppressed).map((g) => g.label)
  const groups = allGroups.filter((g) => !g.suppressed)

  // Only the anchor scored (or nothing left after dropping suppressed cuts) → fall back to the
  // pooled chart, never a dead cut. The suppressed note still names what was dropped.
  if (groups.length <= 1) {
    const pooled = buildScalarIntent(question, anchorResponses)
    return pooled ? (
      <div className="space-y-2">
        <ScalarIntentChart data={pooled} view={view} />
        <SuppressedCutsNote labels={suppressedLabels} />
      </div>
    ) : null
  }

  const scaleMax = groups[0].intent.scaleMax
  const data = groups.map((g) => ({
    label: `${g.label}${g.directional ? ' ~' : ''} (n=${g.intent.n})`,
    value: isTop2 ? g.intent.top2Pct : g.intent.mean,
  }))
  const xAxisMax = isTop2 ? 100 : scaleMax
  const xAxisTitle = isTop2 ? 'Top-2-box %' : `Mean score (1–${scaleMax})`
  // The caveat looks across the CUT bands only (the Everyone anchor is never "thin").
  const anyThin = hasThinCells(groups.filter((g) => g.cutId !== '__all__').map((g) => ({ n: g.intent.n })))

  return (
    <div className="space-y-2" data-testid="qs-scalar-intent-segmented">
      <HBar data={data} colors={data.map(() => colors[0])} autoHeight xAxisTitle={xAxisTitle} xAxisMax={xAxisMax} />
      {anyThin && <BandCaveat />}
      <SuppressedCutsNote labels={suppressedLabels} />
    </div>
  )
}

// QS — segmented small multiples for the monadic per-item rating bar: one ranked per-item
// bar per band (Everyone anchor first, then each selected cut), each scored on that band's
// twins only (re-derived from the per-item raw responses). Mode A stacks every band; Mode B
// toggles one at a time once the anchored bands would run too tall. Every item row carries
// its cell base (n=); bars draw in the single accent hue; a thin-cell caveat closes the
// block when any cell is under THIN_CELL_MIN twins.
// R1.1 — never bar a suppressed (n < CUT_SUPPRESS_N) cut; name it via SuppressedCutsNote.
// When every selected cut is suppressed (or none score), fall back to the pooled Everyone
// bar + note — never a dead chart of thin cuts.
function SegmentedMonadicRatingBar({
  result,
  question,
  cutSlices,
  mode,
  metric,
  colors,
}: {
  result: Result
  question: SurveyQuestion
  cutSlices: Array<CutIdSlice<TwinResponse>>
  mode: 'rows' | 'toggle'
  metric: 'mean' | 'top_2_box'
  colors: string[]
}) {
  const monadicRating = useMemo(() => result.monadic_rating ?? [], [result.monadic_rating])
  const perItem = useMemo(() => result.per_item ?? [], [result.per_item])
  const isTop2 = metric === 'top_2_box'
  // R1.1 — seam already flagged suppressed cuts; drop them before scoring so no thin cut
  // can become a bar. Labels feed the shared warning line.
  const suppressedLabels = useMemo(
    () => cutSlices.filter((s) => s.suppressed).map((s) => s.label),
    [cutSlices],
  )
  const chartableCutIds = useMemo(
    () => cutSlices.filter((s) => !s.suppressed).map((s) => s.cutId),
    [cutSlices],
  )
  const labelFor = useMemo(() => {
    const map = new Map(cutSlices.map((s) => [s.cutId, s.label]))
    return (cid: string) => map.get(cid) ?? cid
  }, [cutSlices])
  // Everyone anchor group: the server per-item aggregate (same math, no slice needed).
  const everyone = useMemo<MonadicSegmentGroup>(
    () => ({
      value: '__all__',
      title: 'Everyone',
      items: monadicRating
        .map((m) => ({ label: m.label, value: isTop2 ? Math.round(m.top_2_box) : m.mean, n: m.n }))
        .sort((a, b) => b.value - a.value),
    }),
    [monadicRating, isTop2],
  )
  const cutGroups = useMemo(
    () => monadicRatingSegmentsByCuts(perItem, monadicRating, chartableCutIds, labelFor, question.name, metric),
    [perItem, monadicRating, chartableCutIds, labelFor, question.name, metric],
  )
  // Ordinal band order for the cut groups; Everyone always leads.
  const bands = useMemo<Array<MonadicSegmentGroup>>(
    () => [everyone, ...breakdownRows(cutGroups.map((g) => ({ label: g.title, group: g }))).map((b) => b.group)],
    [everyone, cutGroups],
  )
  const [active, setActive] = useActiveBand(
    bands.map((g) => ({ cutId: g.value, label: g.title, n: 0, responses: [] })),
  )

  const scaleMax = monadicRating[0]?.scale_max ?? 5
  const xAxisMax = isTop2 ? 100 : scaleMax
  const xAxisTitle = isTop2 ? 'Top-2-box %' : `Mean score (1–${scaleMax})`

  const renderPooled = () => (
    <div data-testid="qs-monadic-rating-bar">
      <HBar
        data={everyone.items.map((it) => ({ label: `${it.label} (n=${it.n})`, value: it.value }))}
        colors={everyone.items.map(() => colors[0])}
        autoHeight
        xAxisTitle={xAxisTitle}
        xAxisMax={xAxisMax}
      />
    </div>
  )

  // Only the anchor remains (or nothing scored after dropping suppressed cuts) → pooled bar,
  // never a dead cut. The suppressed note still names what was dropped.
  if (cutGroups.length === 0) {
    if (everyone.items.length === 0) return null
    return (
      <div className="space-y-2">
        {renderPooled()}
        <SuppressedCutsNote labels={suppressedLabels} />
      </div>
    )
  }

  const anyThin = cutGroups.some((g) => hasThinCells(g.items))

  const renderGroup = (g: MonadicSegmentGroup) => (
    <div className="space-y-1" data-testid="qs-monadic-rating-segment">
      <span className="block truncate text-sm font-medium text-twin-chat-fg" title={g.title}>
        {g.title}
      </span>
      <HBar
        data={g.items.map((it) => ({ label: `${it.label} (n=${it.n})`, value: it.value }))}
        colors={g.items.map(() => colors[0])}
        autoHeight
        xAxisTitle={xAxisTitle}
        xAxisMax={xAxisMax}
      />
    </div>
  )

  if (mode === 'toggle') {
    const shown = bands.find((g) => g.value === active) ?? bands[0]
    const toggleBands = bands.map((g) => ({ cutId: g.value, label: g.title, n: g.items.reduce((a, b) => a + b.n, 0), responses: [] as TwinResponse[] }))
    return (
      <div className="space-y-3" data-testid="qs-monadic-rating-segmented">
        <CutToggle bands={toggleBands} active={shown.value} onSelect={setActive} />
        <SegmentSwap swapKey={shown.value}>{renderGroup(shown)}</SegmentSwap>
        {anyThin && <BandCaveat />}
        <SuppressedCutsNote labels={suppressedLabels} />
      </div>
    )
  }

  return (
    <div className="space-y-6" data-testid="qs-monadic-rating-segmented">
      {bands.map((g) => (
        <div key={g.value}>{renderGroup(g)}</div>
      ))}
      {anyThin && <BandCaveat />}
      <SuppressedCutsNote labels={suppressedLabels} />
    </div>
  )
}

// QS — band-ordered small multiples for the response-derived plots (ranking / single-select
// / pie-as-list). Prepends the "Everyone" anchor, orders the selected cuts ordinally
// (breakdownRows), prints `n=` per band header, and renders each band through the POOLED
// chart (bars in the single accent hue, colors[0] = var(--chart-primary)). Mode A stacks
// every band; Mode B toggles one at a time once the small multiples would run too tall.
// Falls back to one pooled render when nothing slices out, so the control is never a dead end.
function SegmentBandBlocks({
  anchorResponses,
  cutSlices,
  mode,
  renderBand,
}: {
  anchorResponses: TwinResponse[]
  cutSlices: Array<CutIdSlice<TwinResponse>>
  mode: 'rows' | 'toggle'
  /** `cutId` is the band's (`__all__` for the Everyone anchor), for a renderer that draws per cut. */
  renderBand: (bandResponses: TwinResponse[], cutId: string) => ReactNode
}) {
  // R1.1 — the seam already flagged which cuts are too thin to chart at all (`suppressed`,
  // n < CUT_SUPPRESS_N); drop those bands here so no renderer downstream can ever be handed
  // an n=1 read. Suppressed cuts are named in a warning line instead of silently vanishing.
  const allBands = useMemo(() => orderedSegBands(anchorResponses, cutSlices), [anchorResponses, cutSlices])
  const suppressedLabels = useMemo(
    () => allBands.filter((b) => b.cutId !== '__all__' && b.suppressed).map((b) => b.label),
    [allBands],
  )
  const bands = useMemo(() => allBands.filter((b) => !b.suppressed), [allBands])
  const [active, setActive] = useActiveBand(bands)

  // No cuts (only the anchor) → the pooled chart, no band chrome. (A card whose ONLY selected
  // cuts were all suppressed lands here too — the note above still names them.)
  if (bands.length <= 1) {
    return (
      <>
        {renderBand(anchorResponses, '__all__')}
        <SuppressedCutsNote labels={suppressedLabels} />
      </>
    )
  }

  const anyThin = hasThinCells(bands.filter((b) => b.cutId !== '__all__'))

  const bandHeader = (band: SegBand<TwinResponse>) => (
    <div className="flex items-baseline justify-between gap-2">
      <span className="min-w-0 truncate text-sm font-medium text-twin-chat-fg" title={band.label}>
        {band.label}
        {band.directional && (
          <span
            className="ml-1 text-amber-500"
            title="Small base — read as directional only"
            data-testid="qs-segment-band-directional"
          >
            ~
          </span>
        )}
      </span>
      <span className="shrink-0 text-xs tabular-nums text-twin-chat-fg-muted">n={band.n}</span>
    </div>
  )

  if (mode === 'toggle') {
    const shown = bands.find((b) => b.cutId === active) ?? bands[0]
    return (
      <div className="space-y-2" data-testid="qs-segment-band-blocks">
        <CutToggle bands={bands} active={shown.cutId} onSelect={setActive} />
        <SegmentSwap swapKey={shown.cutId}>
          <div className="space-y-1.5" data-testid="qs-segment-band">
            {bandHeader(shown)}
            {renderBand(shown.responses, shown.cutId)}
          </div>
        </SegmentSwap>
        {anyThin && <BandCaveat />}
        <SuppressedCutsNote labels={suppressedLabels} />
      </div>
    )
  }

  return (
    <div className="space-y-6" data-testid="qs-segment-band-blocks">
      {bands.map((band) => (
        <div key={band.cutId} className="space-y-1.5" data-testid="qs-segment-band">
          {bandHeader(band)}
          {renderBand(band.responses, band.cutId)}
        </div>
      ))}
      {anyThin && <BandCaveat />}
      <SuppressedCutsNote labels={suppressedLabels} />
    </div>
  )
}
